// searchCompiler.js — compiles a free-text Desk search query into structured filter
// tokens ("2 room apartment vake under 200k" → rooms=2, kind=apartment, district=VAKE,
// price_max=200000) plus the residual text that didn't compile (which stays a plain
// text search). Deterministic, no AI call, no network — the box must feel instant.
//
// LONG-TERM DESIGN (how this gets smarter over time):
//  - WORDS live in searchVocab.js (data, hand-editable); the ENGINE here only knows
//    shapes (number+unit, direction+amount, name-in-gazetteer). Adding vocabulary —
//    including observed typos from the search_query_log — never touches this file.
//  - Every recognizer consumes SPANS of the input string; later recognizers can't
//    re-read consumed text, so precedence is explicit: specific (number+unit) beats
//    generic (bare number = price). New recognizers slot into RECOGNIZERS in order.
//  - Georgian nouns are matched via the stemmer (core/georgian-stem.mjs — this is the
//    query-side consumer it was written for): ვაკეში/ვაკეზე/ვაკის all hit ვაკე without
//    the vocab having to list inflections.
//  - Output tokens carry a `patch` (fields to set on the panel's advFilters) and a
//    stable `sig` so the UI can render removable chips and remember dismissals.
//
// The compiled result is logged (search_query_log, migration 0049) together with the
// residual — the residual IS the improvement backlog: what users typed that the
// system didn't understand.

import { georgianStem } from '../../core/georgian-stem.mjs'
import * as VOCAB from './searchVocab.js'

// v31 (2026-09-29, public search test of 213 real-style queries): GEORGIAN TYPED IN LATIN LETTERS. "binebi qirith
// tbilisi" stayed Buy with every flat because only a handful of Latin spellings were listed by hand ('qira', 'bina'),
// while the Georgian lists already hold every inflection people use (ბინები, ქირით, ყიდვა, ავეჯით). So a Latin word
// is READ AS GEORGIAN LETTERS and compared with those lists — one generic rule instead of a Latin twin per word.
// Latin spelling is lossy (q = ქ or ყ, t = თ or ტ, k = კ/ქ, p = პ/ფ, ch = ჩ/ჭ, ts = ც/წ), so both sides are compared
// through geLoose(), which folds each such pair to one letter. The fold is only ever applied to a Latin query word
// against Georgian vocabulary — Georgian and English matching are untouched.
const LATIN_GE = [['tch', 'ჭ'], ['sh', 'შ'], ['ch', 'ჩ'], ['kh', 'ხ'], ['gh', 'ღ'], ['zh', 'ჟ'], ['dz', 'ძ'], ['ts', 'ც'], ['th', 'თ'], ['ph', 'ფ'],
  ['a', 'ა'], ['b', 'ბ'], ['g', 'გ'], ['d', 'დ'], ['e', 'ე'], ['v', 'ვ'], ['z', 'ზ'], ['t', 'თ'], ['i', 'ი'], ['k', 'კ'], ['l', 'ლ'], ['m', 'მ'],
  ['n', 'ნ'], ['o', 'ო'], ['p', 'პ'], ['r', 'რ'], ['s', 'ს'], ['u', 'უ'], ['f', 'ფ'], ['q', 'ქ'], ['y', 'ყ'], ['c', 'ც'], ['w', 'წ'], ['x', 'ხ'],
  ['j', 'ჯ'], ['h', 'ჰ']]
export function latinGe(word) {
  if (!/^[a-z]{3,}$/.test(word)) return null
  let out = '', i = 0
  next: while (i < word.length) {
    for (const [l, g] of LATIN_GE) if (word.startsWith(l, i)) { out += g; i += l.length; continue next }
    return null
  }
  return out
}
const GE_LOOSE = { 'ტ': 'თ', 'ყ': 'ქ', 'კ': 'ქ', 'ფ': 'პ', 'ჭ': 'ჩ', 'წ': 'ც', 'ჰ': '' }
export const geLoose = s => [...String(s || '')].map(c => (c in GE_LOOSE ? GE_LOOSE[c] : c)).join('')
// A Latin query word (g = its Georgian reading) against one Georgian vocabulary form and its stem
const latinMatches = (g, gStem, form, formStem) =>
  !!g && /[ა-ჰ]/.test(form) && (geLoose(g) === geLoose(form) || (!!formStem && !!gStem && gStem.length >= 3 && geLoose(gStem) === geLoose(formStem)))
import { axisOf } from './featureAxes.js'
import { APARTMENT_CONTEXT } from './taxonomy.js'
import { toSentence } from './searchSentence.js'
import {
  DEAL_WORDS, KIND_WORDS, CONTEXT_WORDS, SUBTYPE_WORDS, SUBTYPE_PARENT_KIND, ROOM_WORDS, BEDROOM_WORDS, BATHROOM_WORDS, ROOM_PREFIX_LETTERS, STUDIO_WORDS, FLOOR_WORDS,
  AREA_UNITS, PRICE_MAX_WORDS, PRICE_MIN_WORDS, CURRENCY_WORDS,
  NUMBER_WORDS, DISTRICT_ALIASES, ROOM_VALUE_WORDS, VOCAB_VERSION,
  GENERIC_PLACE_WORDS, COMMON_WORD_HOMONYMS, PRICE_BUDGET_WORDS, PRICE_BUDGET_GLUE, MONEY_MULTIPLIERS,
  SEARCH_STOPWORDS, TOP_FLOOR_WORDS, CENTER_WORDS, CENTER_DISTRICT_CODES,
  TBILISI_WORDS, TBILISI_CODE_PREFIXES, AREA_TYPE_WORDS,
  RESTRICTION_ALIASES, RESTRICTION_NON_SUBJECTS, RESTRICTION_ALLOW_WORDS, RESTRICTION_DENY_WORDS,
  FEATURE_NEGATION_BEFORE, FEATURE_NEGATION_AFTER, FEATURE_NEGATION_VERBS, FEATURE_NEGATION_ARTICLES,
  SEARCH_FEATURE_CODES, FEATURE_ALIASES, featureGroupsFrom,
  FLOOR_FLAG_PHRASES, METRO_NEAR_PHRASES, METRO_NEAR_M, AREA_MIN_WORDS, AREA_MAX_WORDS,
  DISTRICT_NEGATION_BEFORE, DISTRICT_NEGATION_AFTER, COUNT_MIN_WORDS, FLOOR_ABOVE_WORDS, FLOOR_BELOW_WORDS, PRICE_PER_WORDS,
} from './searchVocab.js'

// Engine version — bump when RECOGNIZER BEHAVIOR changes (new recognizer, changed
// precedence, changed normalization). Separate from VOCAB_VERSION (words) so a log row
// pins both axes: "district didn't compile" under vocab v3 + engine v2 is a different
// fact than under vocab v9 + engine v2.
export const ENGINE_VERSION = 40   // v40 (2026-11-04, owner "close gaps"): public mode reads a stay length — "for 6 months", "6 თვით", "на 6 месяцев" — as a `lease` token (1-2 digits; the site's min_lease question, core 4232). The Desk is unchanged. v39 (2026-10-31, owner): on the Desk a typed price above every rent in the market (the caller's isSalePrice, from price-sense data) with no deal word also sets the deal to sale ("under 200k" searched rentals too). v38 (2026-10-28, public search audit): word lists match inside unspaced Chinese/Japanese text ("ゴリの土地"), and a Turkish case ending after an apostrophe is consumed with the word before it ("Gori'de"). v37 (2026-10-27, filters audit F08): a price per m² ("1500$ per m2", "1000$ კვ.მ") is the per-m² filter — a `ppm` token (ppmMin/ppmMax) with its unit words consumed; before, the Desk had no such filter and the words were left as text. v36 (2026-10-22, typed-search audit): a 6-8 digit number beside a money word is a price, not a listing id ("150000 დოლარი", "до 150000", "ბიუჯეტი 150000"); a decimal comma is a decimal ("65,5 კვმ"); a fractional count rounds down ("2,5 ოთახი" = 2); a range's multiplier scales its lower number ("150-200k", "150-დან 200 ათასამდე", "от 100 до 150 тысяч"); "between A and B" is a min–max pair; a grouped-thousands money range is never a phone ("100 000 - 150 000 $"); area ranges "50-დან 70 კვმ-მდე"; count minimums ("მინიმუმ 3 ოთახი", "3+ ოთახი"); floors above/below/from ("3 სართულზე მაღლა"); a negated district is the district ✕ ("არა ვაკე", "except vake", "ვაკის გარდა"); a price per m² is not a total price; a currency not typed is not written ("200k-მდე"); compileQuery returns `dropped` — understood spans whose token was not applied ("თბილისი" beside ვაკე) — so a commit can take them out of the box. v35 (2026-10-02, owner): apartment CONTEXT words ("აპარტოტელი", "apart hotel") compile to kind apartment + type_aparthotel_unit, BEFORE the subtype words — "apart hotel" used to search commercial hotels. Desk mode only; the public site has no apart-hotel filter yet. v34 (2026-09-30, reviewer finding): the budget word beside an amount is consumed, not left over as text — after the amount ("200000 budget", "150k budget", "150000 ბიუჯეტი") and with a small word between ("budget of 150000", "ბიუჯეტი არის 150000", "budget around 120000"); the price was always read, the word was not. v33 (2026-09-30, reviewer finding): a NEGATION beside a feature word is read, not ignored — "ლიფტის გარეშე" / "without a lift" / "ლიფტი არ აქვს" searched for flats WITH a lift. Public mode: the feature's yes/no family = no; Desk mode (the RPC has no "without X" verdict): the words stay as text, never the opposite filter. v32 (2026-09-29, owner test 2): public-only feature words (every code the public site filters — project type: ჩეხური/ყავლაშვილის/ხრუშჩოვკა), deal words one letter off (ქირავდებ, qirvdeba), two words typed as one (ბინისყიდვა), a digit as a word edge (qiravdeba1otakhiani), area bounds carried by the unit word (100 კვადრატამდე), a hyphen case ending after understood text (500$-ში), a town name ON something is a street (ყაზბეგზე), a station by its first word beside the metro word (მეტრო ახმეტელთან). v31 (2026-09-29): Georgian typed in Latin letters is read as Georgian letters against the Georgian word lists, place names and feature phrases ("binebi qirith", "qidva", "avejith", "qutaisshi"); the same meaning said twice is consumed, not left over; a Tbilisi-side place beside a named far city is dropped ("ბათუმში ზღვასთან" was Batumi + the Tbilisi Sea). v30 (2026-09-25): numbered sub-areas — a district + a number with its TYPE word ("გლდანის მე-3 მ/რ", "Глдани 3 мкр") is that microdistrict/block/massif, keyed on the rows' reviewed English names; a bare number never picks one. v29 (2026-09-25, public mode): district names in every site language from district_name_i18n (non-Latin always; another locale's Latin name only for that locale's visitor). v28 (2026-09-25): district Russian names indexed (none were — Russian worked only through hand aliases); public mode: the searched city's own neighbourhoods resolve ("gonio" on Batumi); Chinese/Japanese place names matched inside unspaced runs. v27 (2026-09-25): a bare number only the OTHER deal can mean (670 on the Buy tab) is a price in that deal — public mode only. v26 (2026-09-25, public search audit — the public site now compiles through this engine): negated floors ("not first floor" was floor=1), floor and area RANGES and bounds, the condition ladder read from the REGISTRY when given (the constant lacked cls_under_constr/new_build and misnamed reno_renovated), feature names matched without their bracketed qualifier ("White frame (finished shell)" was never typeable), and two PUBLIC-MODE options that leave the Desk byte-identical: bare digits are not listing ids, and a bare deal-plausible number is a max price
// v25 was:   // v25 (2026-09-24): international room conventions read from ROOM_CONVENTIONS (vocab v20) — bathrooms counts, the T3/F3 room prefix, studio as the layout fact
// v24 was:   // v24 (F-100 audit, 2026-09-10): English "5th floor" / "floor 5" shapes; road-type words are generic (F-101, vocab v19)
// v24 also: condition ladder words, portal names, and a bare money-sized range (owner 2026-09-10)
// v23 was:   // v23: CADASTRAL CODES (2026-08-12, B650) - "81.08.13.344" compiled to nothing and fell through to the address haystack, where the dots are stripped and the digits AND-ed as substrings against house numbers; 4,549 properties carry a code and none could be reached by it. Now an IDENTITY token (like listing_id/phone, policy {}, no panel field) resolved DB-wide by /listings/search, which does a PREFIX match because 2,342 of those codes carry more segments than an agent types - and a CONTAINS rather than a left-anchored prefix, because one cadastral_code field may hold several codes for a property spanning two parcels. Runs BEFORE the numeric recognizers: a dotted digit run is the same shape the grouped-thousands price rule reaches for, and spans are first-claim-wins;  v22: INVISIBLE CHARACTERS (2026-08-12, B645) - a phone pasted from WhatsApp/iOS carries NO-BREAK SPACEs and a trailing U+202C POP DIRECTIONAL FORMATTING; every phone pattern ends in (?=\s|$) and U+202C is not in JS \s, so the lookahead failed, removing the spaces did not help, and one BACKSPACE "made it a number" by deleting the invisible char. normalizeInvisible() maps each offender to a single space IN PLACE (never deletes - spans feed the highlight mirror and search_query_log, so length must be preserved) and runs before every recognizer, so pasted district names and listing ids are fixed by the same change;  v21: property SUBTYPES (2026-08-01, B267) - the grammar's Identity layer 3 became typeable. Until now the box FLATTENED it: კოტეჯი/ვილა compiled to kinds:[house], ოფისი/საწყობი/მაღაზია to kinds:[commercial], which is the same collapse B252 spent a day undoing at INGEST, reappearing at query time. A subtype recognizer now runs BEFORE the kind one (spans are first-claim-wins) and its policy emits BOTH subtypes:[token] and kinds:[parent], so precision rises and reach never falls. The RPC verdict is three-valued, so thin-coverage subtypes answer with a small confident set plus a large possible-matches shelf rather than a silent miss. garage/parking_space are deliberately NOT typeable - all 402 ავტოფარეხი text hits are flats WITH a garage, so the words belong to the garage_* feature recognizer (v19), not here;  v20: the home city (2026-07-31) - თბილისი compiles to a districtCodes patch over every TBS_/PERI_ district, because Tbilisi is the ONE Georgian city with no `district` row (it was the implicit root of the 63 TBS_* codes) and so was the #1 unclaimed fragment in the query log - 40 hits in 3 weeks, ~5x the next. As free text it AND-ed against the address haystack, where 12% of properties do not carry the city name, silently excluding them. NOT done as a MUN_TBILISI row: the hierarchy is raion->ubani and dset expands ONE level, so a third level would drop every ubani-coded property. Dropped when a far city is also named, same as center;  v2: unique-prefix districts; v3: shift-pair/phonetic fold matching (ორტაწალა → ორთაჭალა); v4: room value-words (двушка/orotaxiani) + N+1 → bedrooms=N; v5: listing_id (pasted platform URL / bare id); v6: bare numbers no longer guess price (money markers required — unknown ≠ guess); v7: near-location gazetteer (metro/park/mall/complex from the locations dictionary, aliases in locations.meta.aliases); v8: phone tokens (bare 9-15 digits / +00-prefixed / GE mobile with separators — international; bare listing ids narrowed to 7-8 digits); v9: BROWSE_POLICY patches moved to array-form facet fields (intents/kinds/roomsSet/bedroomsSet — checkbox facets 2026-07-17); v10: phone recognizer accepts ANY separator grouping (3-3-3, leading single, parens) not just 5XX-XX-XX-XX — dots excluded so cadastral codes aren't misread; "მეტრო" no longer prefix-matches the Metromsheni district; near-location recognizer is STEM-based (inflected names match: დელისთან→დელისი) with a "მეტრო"/"metro" promoting marker so "მეტრო X" resolves to the STATION even when X is also a district; district recognizer no longer part-indexes compound official names (settlement/municipality/microdistrict/village) so a bare Tbilisi street (ჭავჭავაძის/რუსთაველი/აღმაშენებელი) stops matching a far Rustavi settlement; Tbilisi-primary rule — a district match resolving ONLY to a non-Tbilisi district (not TBS_/PERI_) is suppressed → residual, so Batumi's same-named რუსთაველი/აღმაშენებელი neighborhoods don't hijack a Tbilisi search (2026-07-18); v11: common-word district collision guard (phrase pass + GENERIC_PLACE_WORDS/COMMON_WORD_HOMONYMS — see the note at recognizeDistrict); v12: price recognizer hardening (2026-07-18 hunt, bugs 5/9/10/14/24/27/39/40/50) — grouped thousands ("150 000"/"150,000"/"150.000" parse as 150000, not 0 or a decimal), money multipliers from vocab (კ/ათასი/მილიონი — replaces hardcoded [km]), hyphen-glued direction suffixes ("200000-მდე"/"100000-დან"), numeric ranges ("100k-200k"/"100000-150000$" → min+max pair), budget markers (ფასი/ბიუჯეტი make a bare number money), attached "ლ" ("200000ლ" → GEL), and the phone recognizer skips a digit run followed by a currency/multiplier/direction word so "100000000 დოლარი" is a price, not a phone; v13: floor/inflection/URL pass (2026-07-18 hunt, bugs 6/7/16/17/19/21/29/41/42/46) — numUnit accepts the "მე-" ordinal prefix ("მე-5 სართული" no longer leaks "მე-") and requires a word boundary after the unit ("9 სართულიანში" — a building's storey count — no longer misreads as floor=9; 'სართულიანი' dropped from FLOOR_WORDS, locative 'სართულზე' + ordinal words added in vocab v9); "5/9" floor-of-total notation recognized (floor=5, total not a filter); ss.ge listing URLs extract the hyphen-glued id ("…iyideba-bina-35898814"); near-location district-deferral no longer counts words from compound official far-city names, so bare "რუსთაველი" resolves to the metro like cyrillic "руставели" already did; latin district words strip one transliterated postposition on miss ("vakeshi"/"saburtaloze" now resolve); syncope genitives ისნის/ავლაბრის resolve via the stemmer's SYNCOPE dictionary; v14: minors sweep (2026-07-18 hunt) — SEARCH_STOPWORDS (რაიონში/ან/და…) are dropped from the residual text search (they AND-zeroed queries) but logged in failures as 'stopword'; district words shed trailing periods ("ვაკე. საბურთალო" no longer misses Vake); rooms/bedrooms/floor counts must be positive integers ("2.5 ოთახი"/"0 ოთახი" stay residual, area keeps decimals); v15: top floor (user insight 2026-07-18: floor == floors_total IS "last floor") — "N/M" now emits BOTH floor=N and floors_total=M (so "12/12" inherently filters to top-floor listings via existing RPC params), and "ბოლო სართული"/"top floor" phrases compile to a top_floor token → {topFloor:true} → RPC v11 p_top_floor (floor = floors_total cross-column predicate; null either side = unknown, never mismatch); v16: cities + "the center" (2026-07-18) — the Tbilisi-primary suppression now lets CITY-level codes (MUN_/TWN_) through, so ბათუმი/ქუთაისი/გორი filter as districts (far-city NEIGHBORHOODS — BAT_/KUT_ children like Batumi's რუსთაველი — stay suppressed, which is what the rule was protecting against; on a same-stem city pair TWN_ beats MUN_); "ცენტრი"/"downtown" compiles to one 🏙 token patching districtCodes with CENTER_DISTRICT_CODES (vocab-defined business set), dropped when the query also names a non-Tbilisi city ("ბათუმის ცენტრი" must not inject Tbilisi districts); v17: bare listing ids widened 7-8 → 6-8 digits (2026-07-22: korter ids are SIX digits — 791223-shaped, checked live — so the v8 rule structurally excluded every bare korter id; safe post-v6 because a bare 6-digit number compiled to NOTHING, price needs money markers); v18: restriction subjects (2026-07-28) - the ~46 tenant_terms subjects the Terms facet filters by are now recognized in the box ("indoelebi"/"dzagli"/"studentebi"), compiling to the SAME restrictions filter the sidebar chips write; subject list comes from the live features rows, not a constant, so chip and box share one vocabulary; runs after the place recognizers; single-word subjects match through the Georgian stemmer, multi-word ones as exact phrases (longest first, so "patara dzagli" beats bare "dzagli"); v19: property features (2026-07-29, B37's amenity half) - "ლიფტი"/"aivani"/"parkingi" compile to the SAME featureCodes filter the new Features facet writes, served by p_feature_codes (live in the RPC since 0241, driven by nothing until now). Vocabulary is FEATURE_GROUPS ∩ the live feature rows, so the box can only compile a filter the panel can show; presence-only (the RPC has no "without X" verdict, so the box does not pretend to offer one); runs after the restriction recognizer, which runs after the place recognizers - an amenity word is never a place or a tenant subject

// Vocabulary fingerprint — identity of the ACTUAL words in effect at runtime, not the
// human-maintained version label. Protects the log against forgotten bumps / stray
// edits: same version + different hash = someone edited without bumping. djb2, not a
// crypto hash — this is an identity check, nothing adversarial.
function djb2(str) {
  let h = 5381
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0
  return h.toString(16).padStart(8, '0')
}

// Computed once at module load over every exported vocab structure.
export const VOCAB_HASH = djb2(JSON.stringify(Object.fromEntries(Object.entries(VOCAB).sort())))

const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
// Vocab alternation → regex fragment, longest first so "up to" wins over "up".
const alt = words => [...words].sort((a, b) => b.length - a.length).map(esc).join('|')

// "200k" / "1.5m" / "200,000" / "150 000" / "150.000" → number.
// GROUPED-THOUSANDS RULE (engine v12): digits split into 3-digit groups by space,
// comma, or dot are ONE number ("150 000" was parsing as two numbers — the "000"
// grabbed the currency word and compiled price=0). A separator NOT followed by
// exactly 3 digits stays a decimal ("1.5m" → 1.5 million).
const GROUPED_NUM = /^\d{1,3}(?:[ ,.]\d{3})+$/
// Shared number fragment for money regexes: grouped-thousands first (longest win),
// else plain integer/decimal.
const NUM_FRAG = '\\d{1,3}(?:[ ,.]\\d{3})+|\\d+(?:[.,]\\d+)?'
function amount(numStr, suffix) {
  // (2026-10-22: a comma NOT followed by exactly three digits is a decimal comma — "65,5 კვმ" was 655 m²,
  // "1,5 მილიონი" 15 million, "2,5 სართული" floor 25. "1500,000" style thousands keep their comma.)
  const n = GROUPED_NUM.test(numStr)
    ? parseInt(numStr.replace(/\D/g, ''), 10)
    : parseFloat(numStr.replace(/,(?=\d{3}(?!\d))/g, '').replace(/,/g, '.'))
  if (!Number.isFinite(n)) return null
  const mult = suffix ? MONEY_MULTIPLIERS[suffix.toLowerCase()] : null
  return mult ? n * mult : n
}

// number+unit recognizers share one shape: digits (or a Georgian number word)
// followed by a unit/qualifier word — "3 rooms", "სამ ოთახიანი", "65 კვმ".
function numUnitRegex(unitWords) {
  const units = alt(unitWords.map(w => w.toLowerCase()))
  const kaNums = alt(Object.keys(NUMBER_WORDS))
  // Engine v13: optional "მე-" ordinal prefix consumed ("მე-5 სართული" left "მე-" as
  // residual noise before), and the unit must END the word — "9 სართულიანში" (a
  // 9-STOREY building) used to match the 'სართული' prefix and compile floor=9.
  return new RegExp(`(?:მე-?)?(?:(\\d+(?:[.,]\\d+)?)(?:st|nd|rd|th)?|(${kaNums}))[\\s-]*(?:${units})(?!\\p{L})`, 'giu')
}
function numFromMatch(m) {
  if (m[1] != null) return amount(m[1], null)
  return NUMBER_WORDS[m[2]] ?? null
}

// ── individual recognizers ────────────────────────────────────────────────────
// Each returns [{ start, end, token }]; token = { sig, type, label, patch }.
// `sig` is the dismissal key: same field+value re-typed = same chip identity.

// Count-typed facets must be whole positive numbers — "2.5 ოთახი" / "0 ოთახი" are
// not representable filters (engine v14); area keeps decimals ("45.5 კვმ" is real).
const COUNT_TYPES = new Set(['rooms', 'bedrooms', 'bathrooms', 'floor'])
function recognizeNumUnit(text, unitWords, type, labelFn) {
  const out = []
  // v24: a COUNT range — "2-3 ოთახი", "1-2 bedrooms" — is every value in it (the panel's set boxes), not the
  // upper bound with "2-" left as residual. Rooms/bedrooms only; a floor range is a different shape (min/max).
  if (type === 'rooms' || type === 'bedrooms') {
    const units = alt(unitWords.map(w => w.toLowerCase()))
    const rr = new RegExp(`(?<!\\d)(\\d)\\s*[-–]\\s*(\\d)[\\s-]*(?:${units})(?!\\p{L})`, 'giu')
    let rm
    while ((rm = rr.exec(text))) {
      const a = Number(rm[1]), b = Number(rm[2])
      if (!(a >= 1 && b > a && b <= 9)) continue
      // ONE token for the whole span (the pipeline keeps the first claim per span), carrying the set
      const set = []; for (let v = a; v <= b; v++) set.push(v)
      out.push({ start: rm.index, end: rm.index + rm[0].length, token: { sig: `${type}:${a}-${b}`, type, label: `${a}–${b} ${type}`, value: { n: a, set } } })
    }
    // (2026-10-22: "2,3 ოთახი" is a LIST — 2 or 3 rooms. It read as 23 → the 5+ box; the decimal-comma rule would make it 2.3.)
    const lr = new RegExp(`(?<![\\d.,])(\\d)\\s*,\\s*(\\d)[\\s-]*(?:${units})(?!\\p{L})`, 'giu')
    while ((rm = lr.exec(text))) {
      const a = Number(rm[1]), b = Number(rm[2])
      if (!(a >= 1 && b > a && b <= 9)) continue
      if (b === 5 && /^\d,5/.test(rm[0])) continue   // "2,5" / "1,5" written tight is a half room (rounded down below)
      const set = [a, b]
      out.push({ start: rm.index, end: rm.index + rm[0].length, token: { sig: `${type}:${a},${b}`, type, label: `${a}, ${b} ${type}`, value: { n: a, set } } })
    }
  }
  const re = numUnitRegex(unitWords)
  let m
  while ((m = re.exec(text))) {
    let v = numFromMatch(m)
    if (v == null) continue
    // (2026-10-22: a half room — "2,5 ოთახი" / "2.5 rooms" — is the whole rooms plus a small one, so it rounds DOWN to 2.
    // A fractional FLOOR stays unread: "2,5 სართული" describes a building's storeys, never the floor a flat is on.)
    if (COUNT_TYPES.has(type) && !Number.isInteger(v) && type !== 'floor') v = Math.floor(v)
    if (COUNT_TYPES.has(type) && (!Number.isInteger(v) || v < 1)) continue
    out.push({
      start: m.index, end: m.index + m[0].length,
      token: { sig: `${type}:${v}`, type, label: labelFn(v), value: { n: v } },
    })
  }
  return out
}

// Words that ARE a count (engine v4): "двушка" / "orotaxiani" carry the number
// inside the word — no adjacent digit for recognizeNumUnit to find. Whole-word
// match against ROOM_VALUE_WORDS; emits the SAME token shape as "2 rooms", so
// sig-dedup naturally collapses "двушка 2 комн" to one chip.
function recognizeRoomValueWord(text) {
  const out = []
  for (const [map, type] of [[ROOM_VALUE_WORDS, 'rooms'], [VOCAB.BEDROOM_VALUE_WORDS || {}, 'bedrooms']]) {   // v26: + bedrooms
    if (!Object.keys(map).length) continue
    const re = new RegExp(`(?:^|\\s)(${alt(Object.keys(map))})(?=\\s|$)`, 'giu')
    let m
    while ((m = re.exec(text))) {
      const n = map[m[1]]
      out.push({
        start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
        token: { sig: `${type}:${n}`, type, label: `${n} ${type}`, value: { n } },
      })
    }
  }
  return out
}

// "2+1" / "3+1" (engine v4): the Batumi/Turkish room notation — N bedrooms + a
// living room. The two Gemini vocab runs mapped it to DIFFERENT room counts
// (2-room vs 3-room), proving it's ambiguous as rooms — so it compiles to
// BEDROOMS=N, which both readings agree on. Single digit only, whole-word
// bounded: "100+1" or phone fragments never match.
function recognizeBedroomsPlus(text) {
  const out = []
  const re = /(?:^|\s)([1-9])\s*\+\s*1(?=\s|$)/g
  let m
  while ((m = re.exec(text))) {
    const n = Number(m[1])
    out.push({
      start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
      token: { sig: `bedrooms:${n}`, type: 'bedrooms', label: `${n} bedrooms`, value: { n } },
    })
  }
  return out
}

// "T3" / "F2" (engine v25, vocab v20 ROOM_CONVENTIONS form 'prefix'): the French room notation — a letter BEFORE the
// count, which the number-then-word shape cannot see. TOTAL rooms (living room included), never bedrooms. Single digit
// 1-7, whole-word bounded both sides, so "T34" or a street code stays residual.
function recognizeRoomPrefix(text) {
  const out = []
  if (!ROOM_PREFIX_LETTERS.length) return out
  const re = new RegExp(`(?<![\\p{L}\\d])(${alt(ROOM_PREFIX_LETTERS)})([1-7])(?![\\p{L}\\d])`, 'giu')
  let m
  while ((m = re.exec(text))) {
    const n = Number(m[2])
    out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `rooms:${n}`, type: 'rooms', label: `${n} rooms`, value: { n } } })
  }
  return out
}

// "studio" (engine v25, vocab v20 form 'flag'): the recorded LAYOUT fact (feature layout_studio), never a room count —
// studio ≠ reliably 1 room in this market, which is why ROOM_VALUE_WORDS still leaves it out. Whole word.
function recognizeStudio(text) {
  const out = []
  if (!STUDIO_WORDS.length) return out
  const re = new RegExp(`(?<![\\p{L}\\d])(${alt(STUDIO_WORDS)})(?![\\p{L}\\d])`, 'giu')
  let m
  while ((m = re.exec(text))) out.push({ start: m.index, end: m.index + m[0].length, token: { sig: 'studio', type: 'studio', label: 'studio', value: {} } })
  return out
}

// "5/9" — floor 5 of a 9-storey building (engine v13): one of the most common ways
// agents state a floor, previously residual. Guards: 1-2 digits each, floor ≤ total,
// total ≤ 60 — so dates and odd fractions stay residual.
// Engine v15: the TOTAL is a filter too (floors_total params exist since RPC v6) —
// "N/M" emits BOTH tokens, and "12/12" therefore inherently means TOP floor (the
// user's 2026-07-18 insight: floor == floors_total IS the last floor).
function recognizeFloorOfTotal(text) {
  const out = []
  const re = /(?:^|\s)(\d{1,2})\s*\/\s*(\d{1,2})(?=\s|$)/g
  let m
  while ((m = re.exec(text))) {
    const f = Number(m[1]), t = Number(m[2])
    if (f < 1 || f > t || t > 60) continue
    const numStart = m.index + m[0].indexOf(m[1])
    const slash = m.index + m[0].indexOf('/')
    out.push({
      start: numStart, end: numStart + m[1].length,
      token: { sig: `floor:${f}`, type: 'floor', label: `floor ${f}`, value: { n: f } },
    })
    // (2026-10-22: the floor word after "5/9" belongs to it — "5/9 სართული" left "სართული" as an AND text search)
    const unit = new RegExp(`^\\s*(?:${alt(FLOOR_WORDS.map(w => w.toLowerCase()))})(?!\\p{L})`, 'iu').exec(text.slice(m.index + m[0].length))
    out.push({
      start: slash, end: m.index + m[0].length + (unit ? unit[0].length : 0),
      token: { sig: `floors_total:${t}`, type: 'floors_total', label: `${t}-storey`, value: { n: t } },
    })
  }
  return out
}

// "ბოლო სართული" / "top floor" (engine v15) — a cross-column predicate (the unit's
// floor equals the building's floors_total), not a number. Whole-phrase match.
// v24 (F-100 audit): English word order puts the unit first — "floor 5". Only for floor, only for a
// bare 1-2 digit number, and never when a "/" follows (that is floor-of-total's shape, claimed earlier).
// "for 6 months" / "6 თვით" / "на 6 месяцев" (engine v40, public mode, core 4232): how long the visitor will rent — the
// site's min_lease question. ⚠️ 1-2 digits only (1..36): a monthly RENT is "800 a month" / "1200$ თვეში", 3+ digits,
// and must stay a price. "თვეში" ("in N months" / "a month") is never a lease word.
const LEASE_MONTH_RE = /(?<![\p{L}\d$€₾.,])(?:(?:for|minimum|min\.?|at least|на|минимум|от|მინიმუმ)\s+)?(\d{1,2})\s*-?\s*(?:months?|mos?\.?|месяц(?:а|ев)?|мес\.?|თვით|თვიანი|თვის ვადით|თვე)(?!\p{L})/giu
function recognizeLeaseMonths(text) {
  const out = []
  let m
  LEASE_MONTH_RE.lastIndex = 0
  while ((m = LEASE_MONTH_RE.exec(text))) {
    const n = Number(m[1]); if (!Number.isInteger(n) || n < 1 || n > 36) continue
    out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `lease:${n}`, type: 'lease', label: `${n} months`, value: { months: n } } })
  }
  return out
}

function recognizeUnitFirstFloor(text) {
  const out = []
  const re = /(?<!\p{L})(?:floor|этаж)\s+(\d{1,2})(?![\d/])/giu
  let m
  while ((m = re.exec(text))) {
    const v = Number(m[1]); if (!Number.isInteger(v) || v < 1) continue
    out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `floor:${v}`, type: 'floor', label: `floor ${v}`, value: { n: v } } })
  }
  return out
}
function recognizeTopFloor(text) {
  const out = []
  const re = new RegExp(`(?:^|\\s)(${alt(TOP_FLOOR_WORDS)})(?=\\s|$)`, 'giu')
  let m
  while ((m = re.exec(text))) {
    out.push({
      start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
      token: { sig: 'top_floor', type: 'top_floor', label: '⬆ top floor', value: {} },
    })
  }
  return out
}

// ── v26: negated floors — "not first floor" / "არა პირველი სართული" / "не первый этаж" ────────────
// Runs before every floor recognizer: the phrase CONTAINS "first floor", and first claim wins, so
// without this it compiled to floor = 1 — the opposite of what was asked (public audit B-FLOOR-1).
function recognizeFloorFlag(text) {
  const out = []
  for (const [flag, words] of Object.entries(FLOOR_FLAG_PHRASES)) {
    const re = new RegExp(`(?:^|\\s)(${alt(words.map(w => w.toLowerCase()))})(?=\\s|$|[.,;!?])`, 'giu')
    let m
    while ((m = re.exec(text))) {
      out.push({
        start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
        token: { sig: `floor_flag:${flag}`, type: 'floor_flag', label: flag === 'not_first' ? '≠ 1st floor' : '≠ top floor', value: { flag } },
      })
    }
  }
  return out
}

// ── v36 (2026-10-22): a floor ABOVE / BELOW / FROM / UP TO a number ─────────────────────────────────
// "3 სართულზე მაღლა" was floor = 3 exactly with "მაღლა" left as text; "მე-3 სართულიდან" read nothing.
// Above n is n+1 and up (the Desk's floor range is inclusive); below n is up to n-1.
function recognizeFloorBound(text) {
  const out = []
  const units = alt(FLOOR_WORDS.map(w => w.toLowerCase()))
  const push = (m, n, open) => {
    if (!Number.isInteger(n) || n < 1) return
    const start = m.index + (m[0].length - m[0].trimStart().length)
    out.push({ start, end: m.index + m[0].length, token: { sig: `floor:${open}:${n}`, type: 'floor', label: `floor ${open === 'min' ? '≥' : '≤'} ${n}`, value: { n, open } } })
  }
  const num = '(?:მე-?)?(\\d{1,2})(?:-?ე)?'
  const aboveBelow = new RegExp(`(?:^|\\s)${num}[\\s-]*(?:${units})\\s+(?:(${alt(FLOOR_ABOVE_WORDS)})|(${alt(FLOOR_BELOW_WORDS)}))(?!\\p{L})`, 'giu')
  let m
  while ((m = aboveBelow.exec(text))) push(m, m[2] ? Number(m[1]) + 1 : Number(m[1]) - 1, m[2] ? 'min' : 'max')
  const caseBound = new RegExp(`(?:^|\\s)${num}[\\s-]*(?:(სართულიდან|sartulidan)|(სართულამდე|sartulamde))(?!\\p{L})`, 'giu')
  while ((m = caseBound.exec(text))) push(m, Number(m[1]), m[2] ? 'min' : 'max')
  return out
}

// ── v36 (2026-10-22): a COUNT minimum — "მინიმუმ 3 ოთახი", "at least 2 bedrooms", "3+ ოთახი" ──────────
// Was rooms = 3 exactly with "მინიმუმ" left over as text. The minimum is every box from n up to 5+.
function recognizeCountMin(text, unitWords, type) {
  const out = []
  const units = alt(unitWords.map(w => w.toLowerCase()))
  const res = [
    new RegExp(`(?:^|\\s)(?:${alt(COUNT_MIN_WORDS)})\\s+(\\d)[\\s-]*(?:${units})(?!\\p{L})`, 'giu'),
    new RegExp(`(?<![\\d+])(\\d)\\s*\\+\\s*(?:${units})(?!\\p{L})`, 'giu'),
  ]
  for (const re of res) {
    let m
    while ((m = re.exec(text))) {
      const n = Number(m[1]); if (n < 1) continue
      const set = []; for (let v = Math.min(n, 5); v <= 5; v++) set.push(v)
      const start = m.index + (m[0].length - m[0].trimStart().length)
      out.push({ start, end: m.index + m[0].length, token: { sig: `${type}:${n}+`, type, label: `${n}+ ${type}`, value: { n, set } } })
    }
  }
  return out
}

// ── v26: floor RANGE — "1-3 floor", "floors 2-5", "3-5 სართული" ────────────────────────────────────
// Before the single number+unit shape, which read "1-3 floor" as floor 3 with "1-" left over.
function recognizeFloorRange(text) {
  const out = []
  const units = alt(FLOOR_WORDS.map(w => w.toLowerCase()))
  const res = [
    new RegExp(`(?<![\\d.])(\\d{1,2})\\s*[-–]\\s*(\\d{1,2})(?:st|nd|rd|th)?[\\s-]*(?:${units})(?!\\p{L})`, 'giu'),
    new RegExp(`(?:^|\\s)(?:${units})\\s+(\\d{1,2})\\s*[-–]\\s*(\\d{1,2})(?![\\d.])`, 'giu'),
  ]
  for (const re of res) {
    let m
    while ((m = re.exec(text))) {
      const a = Number(m[1]), b = Number(m[2])
      if (!(a >= 0 && b > a)) continue
      const start = m.index + (m[0].length - m[0].trimStart().length)
      out.push({ start, end: m.index + m[0].length, token: { sig: `floor:${a}-${b}`, type: 'floor', label: `floor ${a}–${b}`, value: { n: a, min: a, max: b } } })
    }
  }
  return out
}

// ── v26: area RANGE and BOUNDS — "70-90 sqm", "at least 100 m²", "до 60 м2" ─────────────────────────
// Before the single number+unit shape, which turned every one of these into a ±10 % window around the
// last number and left the bound word over (public audit B-AREA-1).
function recognizeAreaRange(text) {
  const out = []
  const units = alt(AREA_UNITS.map(w => w.toLowerCase()))
  const num = '(\\d+(?:[.,]\\d+)?)'
  const range = new RegExp(`(?<![\\d.])${num}\\s*(?:${units})?\\s*[-–]\\s*${num}\\s*(?:${units})(?!\\p{L})`, 'giu')
  let m
  while ((m = range.exec(text))) {
    const a = amount(m[1], null), b = amount(m[2], null)
    if (a == null || b == null || !(b > a)) continue
    out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `area:${a}-${b}`, type: 'area_range', label: `${a}–${b} m²`, value: { min: a, max: b } } })
  }
  // (2026-10-22: "100 მ2-ზე მეტი" / "60 კვმ-ზე ნაკლები" — more / less than, after the unit)
  const moreLess = new RegExp(`(?<![\\d.])${num}\\s*(?:${units})\\s*-?ზე\\s+(?:(მეტი|მეტ|დიდი)|(ნაკლები|ნაკლებ|პატარა))(?!\\p{L})`, 'giu')
  while ((m = moreLess.exec(text))) {
    const v = amount(m[1], null)
    if (v == null) continue
    const bound = m[2] ? 'min' : 'max'
    out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `area:${bound}:${v}`, type: 'area_range', label: `${bound === 'min' ? '≥' : '≤'} ${v} m²`, value: bound === 'min' ? { min: v } : { max: v } } })
  }
  // (2026-10-22: "ბინა 50-დან 70 კვმ-მდე" read area ≤ 70 and a PRICE ≥ 50 — the from-word range carried by the unit)
  const upto = alt([...VOCAB.AREA_UNITS_UPTO, ...AREA_UNITS.map(u => `${u}-მდე`), ...AREA_UNITS.map(u => `${u}მდე`)].map(w => w.toLowerCase()))
  const fromTo = new RegExp(`(?<![\\d.])${num}\\s*(?:${units})?\\s*-?(?:დან|from)\\s+(?:to\\s+)?${num}\\s*(?:(?:${upto})|(?:${units}))(?!\\p{L})`, 'giu')
  while ((m = fromTo.exec(text))) {
    const a = amount(m[1], null), b = amount(m[2], null)
    if (a == null || b == null || !(b > a)) continue
    out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `area:${a}-${b}`, type: 'area_range', label: `${a}–${b} m²`, value: { min: a, max: b } } })
  }
  // v32: "100 კვადრატამდე" / "50 კვადრატიდან" — the bound is the unit word's case ending
  for (const [words, bound] of [[VOCAB.AREA_UNITS_UPTO, 'max'], [VOCAB.AREA_UNITS_FROM, 'min']]) {
    const re = new RegExp(`(?<![\\d.])${num}\\s*-?(${alt(words.map(w => w.toLowerCase()))})(?!\\p{L})`, 'giu')
    while ((m = re.exec(text))) {
      const v = amount(m[1], null)
      if (v == null) continue
      out.push({ start: m.index, end: m.index + m[0].length, token: { sig: `area:${bound}:${v}`, type: 'area_range', label: `${bound === 'min' ? '≥' : '≤'} ${v} m²`, value: bound === 'min' ? { min: v } : { max: v } } })
    }
  }
  for (const [words, bound] of [[AREA_MIN_WORDS, 'min'], [AREA_MAX_WORDS, 'max']]) {
    const re = new RegExp(`(?:^|\\s)(${alt(words.map(w => w.toLowerCase()))})\\s*${num}\\s*(?:${units})(?!\\p{L})`, 'giu')
    while ((m = re.exec(text))) {
      const v = amount(m[2], null)
      if (v == null) continue
      const start = m.index + m[0].indexOf(m[1])
      out.push({ start, end: m.index + m[0].length, token: { sig: `area:${bound}:${v}`, type: 'area_range', label: `${bound === 'min' ? '≥' : '≤'} ${v} m²`, value: bound === 'min' ? { min: v } : { max: v } } })
    }
  }
  return out
}

// ── v26 (public mode only): "near metro" with no station — a distance band ─────────────────────────
function recognizeMetroNear(text) {
  const out = []
  const re = new RegExp(`(?:^|\\s)(${alt(METRO_NEAR_PHRASES.map(w => w.toLowerCase()))})(?=\\s|$|[.,;!?])`, 'giu')
  let m
  while ((m = re.exec(text))) {
    out.push({ start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
      token: { sig: 'metro_near', type: 'metro_near', label: `🚇 ≤ ${METRO_NEAR_M} m`, value: { max_m: METRO_NEAR_M } } })
  }
  return out
}

// ── v26 (public mode only, owner 2026-09-25): a lone deal-plausible number is a MAX price ────────────
// The Desk's own ruling since engine v6 is "bare numbers are not prices — unknown ≠ guess", and the Desk
// keeps it: this runs only when the caller passes `bareNumberPrice`. The public owner ruling is the
// opposite for visitors: "2 bed 600" in a rent search means ≤ 600. Guarded twice so it is a reading and
// not a lottery: it runs LAST (every unit, count, id and place has had its claim), and the number must
// sit in the deal's plausible band. The token says it was inferred (confidence medium), and names no
// currency, so the caller's selected currency applies — an explicit currency is claimed by recognizePrice
// long before this runs.
export const BARE_PRICE_BANDS = { rent: [100, 20000], sale: [10000, 5000000], daily_rent: [15, 2000] }
// v27 (2026-09-25, owner screenshot "2 bed 670" on the default Buy tab → 2,541 sale flats and a bare „670"):
// when the TEXT names no deal and the number is implausible for the context's deal but plausible for exactly
// the other one of rent/sale, the number is a price in THAT deal — the token carries `deal` and the adapter
// switches the deal (medium confidence, an editable chip). A deal word in the text is never overridden, and a
// number both bands accept (10,000–20,000) keeps the context's deal.
const OTHER_DEAL = { rent: 'sale', sale: 'rent' }
const inBand = (v, b) => !!b && v >= b[0] && v <= b[1]
function recognizeBareNumberPrice(text, deal, dealStated = false) {
  const band = BARE_PRICE_BANDS[deal]
  if (!band) return []
  const other = dealStated ? null : OTHER_DEAL[deal]
  const out = []
  // grouped thousands only with a 2-3 digit lead group: "2 600" is two numbers (2 bedrooms-ish, then 600), "120 000" is one
  const re = /(?:^|[\s,;])(\d{2,3}(?:[ ,.]\d{3})+|\d+)(?=$|[\s,;!?])/gu
  let m
  while ((m = re.exec(text))) {
    const v = amount(m[1], null)
    if (v == null || !Number.isInteger(v)) continue
    const switched = !inBand(v, band) && other && inBand(v, BARE_PRICE_BANDS[other]) ? other : null
    if (!inBand(v, band) && !switched) continue
    const start = m.index + m[0].indexOf(m[1])
    out.push({ start, end: start + m[1].length, token: { sig: `price:max:${v}`, type: 'price',
      label: `≤ ${v.toLocaleString('en-US')}`, value: { bound: 'max', amount: v, currency: 'USD', currencySpoken: false, inferred: true, ...(switched ? { deal: switched } : {}) } } })
  }
  return out
}

// Pasted platform link / bare listing id (engine v5): every myhome/ss/korter listing URL
// carries the platform's numeric listing id — paste the link into the box, get the ad.
// The WHOLE URL span is consumed, not just the id: myhome slugs like
// "/pr/25385826/qiravdeba-2-otaxiani-..." would otherwise leak "2-otaxiani" to the rooms
// recognizer and quietly filter everything to 2-room. Id extraction = first /<5+ digits>
// path segment — keep in sync with the server's extractAnchors/listings-search regex
// (lib/server.js /api/core/listings/search): match the id segment, never the URL prefix
// (myhome renamed /pr/ → /udzravi-qoneba/ once already, 2026-06-24).
// Bare numbers: 7+ digits whole-word only get an ID CHIP here. 5-6 digit bare numbers are
// ambiguous (short platform ids vs typed prices) — as of engine v6 they compile to NOTHING
// (recognizePrice now genuinely requires a money marker; its old bare-1000..10M band
// misparsed pasted 6-digit ids as a price ceiling) and fall to residual text search, which
// matches them against listing identity (source_listing_id / source_url) in IntakeTab.
// ── v36 (2026-10-22): numbers a MONEY word stands beside ─────────────────────────────────────────────
// A currency, multiplier, direction or budget word right before or after a number (or a number range) marks it as
// money. The identity recognizers (bare listing id, phone) run before recognizePrice and are first-claim-wins, so
// they must step aside for these spans: "150000 დოლარი", "до 150000", "ბიუჯეტი 150000" were listing ids, and
// "100 000 - 150 000 $" a phone. Returns [{start,end}] of the number part.
let MONEY_RE_ = null
function moneyMarkedSpans(text) {
  if (!MONEY_RE_) {
    const cur = [...CURRENCY_WORDS.USD, ...CURRENCY_WORDS.GEL]
    const dir = [...PRICE_MAX_WORDS, ...PRICE_MIN_WORDS]
    const before = alt([...cur, ...dir, ...PRICE_BUDGET_WORDS])
    const after = alt([...cur, ...dir, ...PRICE_BUDGET_WORDS, ...Object.keys(MONEY_MULTIPLIERS)])
    const glue = alt(PRICE_BUDGET_GLUE)
    const numg = '\\d{1,3}(?:[ ,.]\\d{3})+|\\d+'
    const run = `(?:${numg})(?:\\s*[-–—]\\s*(?:${numg}))?`
    MONEY_RE_ = [
      new RegExp(`(?<![\\p{L}\\p{N}])(?:${before})(?:\\s+(?:${glue}))?[\\s:=-]*(${run})(?![\\p{N}])`, 'giu'),
      new RegExp(`(?<![\\p{L}\\p{N}])(${run})[\\s-]*(?:${after})(?![\\p{L}])`, 'giu'),
    ]
  }
  const out = []
  for (const re of MONEY_RE_) {
    re.lastIndex = 0
    let m
    while ((m = re.exec(text))) {
      const at = m.index + m[0].indexOf(m[1])
      out.push({ start: at, end: at + m[1].length })
      re.lastIndex = m.index + Math.max(1, m[0].length)
    }
  }
  return out
}
const touchesAny = (spans, start, end) => spans.some(s => s.start < end && start < s.end)

function recognizeListingRef(text, opts = {}) {
  const out = []
  const url = /(?:https?:\/\/)?\S*(?:myhome\.ge|ss\.ge|korter\.ge)\/\S+/giu
  let m
  while ((m = url.exec(text))) {
    // Id = first /<5+ digits> path segment; FALLBACK (engine v13): ss.ge glues the id
    // to the slug with a hyphen ("…/iyideba-bina-35898814") — the same platform quirk
    // that broke reshare registration (see CLAUDE.md) — so also accept a trailing
    // -<5+ digits> at the end of the path.
    const idm = m[0].match(/\/(\d{5,})(?:\D|$)/) || m[0].match(/-(\d{5,})(?:[/?#]\S*)?$/)
    if (!idm) continue
    out.push({
      start: m.index, end: m.index + m[0].length,
      token: { sig: `listing_id:${idm[1]}`, type: 'listing_id', label: `🔗 ID ${idm[1]}`, value: { id: idm[1] } },
    })
  }
  // 6-8 digits (engine v17): platform ids are 7-8 digits on myhome/ss but SIX on korter
  // (checked live 2026-07-22: korter source_listing_ids are 791223-shaped), so the old 7-8
  // rule structurally excluded every bare korter id. Widening to 6 is safe NOW in a way it
  // wasn't at v8: engine v6 already stripped bare numbers from the price recognizer (money
  // markers required), so a bare 6-digit number compiled to NOTHING — chip beats nothing,
  // and the chip is dismissible. 9+ digits is a PHONE (recognizePhone below) — the case
  // "995579283169" used to become an ID chip here and matched nothing.
  const bare = /(?:^|\s)(\d{6,8})(?=\s|$)/g
  const money = opts.publicMode ? [] : moneyMarkedSpans(text)
  // v26: the public site has no portal ids to look up — a bare 6-8 digit number there is a price or nothing
  while (!opts.publicMode && (m = bare.exec(text))) {
    const at = m.index + m[0].indexOf(m[1])
    // v36 (2026-10-22): beside a money word it is the price, not an id — "150000 დოლარი", "max 150000", "ბიუჯეტი 150000"
    if (touchesAny(money, at, at + m[1].length)) continue
    out.push({
      start: at, end: m.index + m[0].length,
      token: { sig: `listing_id:${m[1]}`, type: 'listing_id', label: `🔗 ID ${m[1]}`, value: { id: m[1] } },
    })
  }
  return out
}

// Cadastral code (engine v23, B650 — owner 2026-08-12: "search cant reqognise cadastral codes to
// filtr by 81.08.13.344") → identity chip, resolved DB-wide through /listings/search, exactly like
// a listing id or a phone. 4,549 properties carry a code and not one could be reached by it.
//
// A code is a parcel's legal NAME, so this is an identity lookup and not a filter: it has no panel
// field (policy `{}` below) and it rides the same reach-back that expands a hit into its whole
// lineage family. The server does the prefix work — 2,342 stored codes carry more segments than an
// agent types, and a code may share its field with another (one property, two parcels).
//
// Shape: three groups minimum (`01.14.05`), then any number of further dot-separated segments,
// which may end in a Georgian or Latin letter (`…003ა`, `…007a`). Requiring three groups keeps a
// bare "01.14" from sweeping a district into an identity answer.
// ⚠️ Runs BEFORE the numeric recognizers — spans are first-claim-wins, and a dotted digit run is
// exactly the shape the grouped-thousands price rule ("150.000") reaches for.
// ── F-103 (2026-09-10): THE PORTAL'S OWN ENQUIRY TEMPLATE, PASTED WHOLE ──────────────────────
// korter's "I'm interested" button sends the agent a canned message in one of three languages —
//   "Hello, I am interested in the in Lech Kaczyński St, 8. Listing link: https://korter.ge/…/879430"
//   "Здравствуйте, меня интересует квартира в ул. Отара Чиладзе, 166 корп. 1. Ссылка на объявление: …"
//   "გამარჯობა, მე მაინტერესებს ბახტრიონის ქუჩა, 30-ში. განცხადების ბმული: …"
// — and agents paste it into the box to find WHICH listing the client means. The link was already
// claimed as the listing id, but the prose around it was not: it went to the server as fuzzy text
// AND its words compiled to extra filters ("квартира" → kind, "Vake" → district) that were ANDed
// onto the exact identity. Measured in the log (30 days): 50 pastes, 49 with a link; the answers were
// 217 / 198 / 196 rows for one flat, and 0 for nine flats that core actually holds.
// When a link follows, the whole template is ONE token with an empty patch, so the id is the entire
// search — exactly what the agent wanted. Without a link (1 of 50) only the greeting is claimed and the
// street and number stay as text, where the street door can still answer.
// Flags: i + u; the classes are written out ([0-9], [^.,]) so no escape can be lost in transit.
const ENQUIRY_RES = [
  /(?:^|[ 	])(hello,?[ 	]+i[ 	]+am[ 	]+interested[ 	]+(?:in[ 	]+)+(?:the[ 	]+)?(?:in[ 	]+)?)([^,]+?),[ 	]*([0-9].*?)\.?[ 	]*(listing[ 	]+link:?)/iu,
  /(?:^|[ 	])(здравствуйте,?[ 	]+меня[ 	]+интересует[ 	]+(?:квартира[ 	]+)?(?:в[ 	]+)?(?:ул\.?[ 	]*)?)([^,]+?),[ 	]*([0-9].*?)\.?[ 	]*(ссылка[ 	]+на[ 	]+объявление:?)/iu,
  /(?:^|[ 	])(გამარჯობა,?[ 	]+მე[ 	]+მაინტერესებს[ 	]+)([^,]+?),[ 	]*([0-9].*?)(?:-ში)?\.?[ 	]*(განცხადების[ 	]+ბმული:?)/iu,
]
function recognizeEnquiryPaste(text) {
  const out = []
  for (const re of ENQUIRY_RES) {
    const m = re.exec(text)
    if (!m) continue
    const street = m[2].trim(), number = m[3].trim()
    const full = m.index + m[0].indexOf(m[1])
    const hasLink = /https?:\/\//i.test(text.slice(m.index + m[0].length))
    // with a link: the whole template, greeting through "link:", is one claimed span (the id is the search)
    // without one: only the greeting, so "<street>, <number>" stays as text for the street door
    const end = hasLink ? m.index + m[0].length : full + m[1].length
    out.push({ start: full, end, token: { sig: 'enquiry:paste', type: 'enquiry', label: `✉ ${street}, ${number}`, value: { street, number, hasLink } } })
    break
  }
  return out
}
const CADASTRAL_RE = /(?:^|\s)(\d{2}\.\d{2}\.\d{2}(?:\.[0-9a-zႠ-ჿ]+)*)(?=\s|$)/gi
function recognizeCadastral(text) {
  const out = []
  let m
  CADASTRAL_RE.lastIndex = 0
  while ((m = CADASTRAL_RE.exec(text))) {
    const code = m[1]
    out.push({
      start: m.index + m[0].indexOf(code), end: m.index + m[0].length,
      token: { sig: `cadastral:${code}`, type: 'cadastral', label: `🧾 ${code}`, value: { code } },
    })
  }
  return out
}

// Phone number (engine v8) → identity chip, resolved DB-wide through /listings/search
// (which suffix-matches the digits against party phones — see the server's last-9 rule).
// International by design (user requirement — clients/owners are NOT only Georgian):
//   A. +/00-prefixed with any separators: +995 579 28 31 69, +44 20 7946 0958, 0049 30 …
//   B. bare / separator-grouped 9-15 digit run in ANY grouping agents use: 995579283169,
//      579283169, 579 283 169, 579 28 31 69, 5 79 28 31 69, 579-28-31-69, (595) 12 34 56
// sig keys on the LAST 9 digits so "+995579283169", "995579283169" and "579283169" are one
// chip — same normalization the server matches by. patch = {} (identity filter, like
// listing_id: nothing to set on the panel; the match lives in IntakeTab's reach-back).
function recognizePhone(text) {
  const out = []
  // Money guard (engine v12): a digit run immediately followed by a currency /
  // multiplier / direction word is a PRICE ("100000000 დოლარი" — a 9-digit sale
  // amount — used to become a phone chip and the budget was lost). Applies to the
  // unprefixed passes only; an explicit +/00 prefix stays a phone regardless.
  const moneyAfter = new RegExp(
    `^\\s*(?:${alt([...CURRENCY_WORDS.USD, ...CURRENCY_WORDS.GEL, ...Object.keys(MONEY_MULTIPLIERS), ...PRICE_MAX_WORDS, ...PRICE_MIN_WORDS])})`, 'iu')
  // v36 (2026-10-22): …and a run inside a money-marked number or range is never a phone, prefixed or not — in
  // "100 000 - 150 000 $" the "000 - 150 000" read as a 00-prefixed international number (and "500 000 - 550 000 $"
  // as a 5-led Georgian mobile). A real "+995 …" never sits inside a money span.
  const money = moneyMarkedSpans(text)
  const push = (start, end, rawDigits, { guard = false } = {}) => {
    if (guard && moneyAfter.test(text.slice(end))) return
    if (!rawDigits.startsWith('+') && touchesAny(money, start, end)) return
    const digits = rawDigits.replace(/\D/g, '')
    if (digits.length < 8 || digits.length > 15) return
    out.push({
      start, end,
      token: { sig: `phone:${digits.slice(-9)}`, type: 'phone', label: `📞 ${digits}`, value: { digits } },
    })
  }
  let m
  // A. +/00-prefixed international, any separators.
  const prefixed = /(?:^|\s)((?:\+|00)\d[\d\s\-().]{5,}\d)(?=\s|$)/g
  while ((m = prefixed.exec(text))) push(m.index + m[0].indexOf(m[1]), m.index + m[0].length, m[1])
  // B. SOLID 9-15 digit run (no separators) — a pasted number, incl. solid international.
  // (Pre-v10 behavior, unchanged; a solid run can't collide with grouped prices/cadastral.)
  const solid = /(?:^|\s)(\d{9,15})(?=\s|$)/g
  while ((m = solid.exec(text))) push(m.index + m[0].indexOf(m[1]), m.index + m[0].length, m[1], { guard: true })
  // C. Separator-GROUPED run (engine v10): a Georgian mobile — 9 digits starting with 5 — in
  // ANY grouping agents paste: "579 283 169", "5 79 28 31 69", "(595) 12 34 56", "579-28-31-69".
  // The `5` lead is a DATA-DRIVEN guard: every phone contact in core is a 9-digit 5-mobile (0
  // landlines/8-digit), so requiring it keeps space-grouped PRICES ("100 000 000" → starts 1)
  // and space-delimited CADASTRAL ("01 10 14 023" → starts 0) from being misread as phones —
  // the two 9+-digit false positives the broad v10 regex let through. Dotted cadastral is
  // already safe (dots aren't in the separator class). Grouped INTERNATIONAL must carry a +/00
  // (A) or be pasted solid (B) — an accepted, rare trade for that safety.
  const geGrouped = /(?:^|\s)(\(?5(?:[\d\s\-()]*\d){8})(?=\s|$)/g
  while ((m = geGrouped.exec(text))) push(m.index + m[0].indexOf(m[1]), m.index + m[0].length, m[1], { guard: true })
  return out
}

// Money: [budget word]? [direction word]? amount [multiplier]? [currency]? [direction]?
// — any adjacent order variants covered by before/after groups; direction words may be
// hyphen-glued ("200000-მდე", engine v12). Three passes, ranges first:
//  1. numeric RANGE "A-B" with a money marker → a min+max token PAIR (was: two
//     conflicting max chips / a scrambled bound);
//  2. the single-amount shape;
//  3. attached "ლ" ("200000ლ") — kept out of CURRENCY_WORDS because a one-letter
//     word with a space gap would claim any number before a ლ-initial word.
function recognizePrice(text) {
  const out = []
  const cur = `(${alt(CURRENCY_WORDS.USD)}|${alt(CURRENCY_WORDS.GEL)})`
  const dirMax = alt(PRICE_MAX_WORDS), dirMin = alt(PRICE_MIN_WORDS)
  // Multiplier suffix must not be the start of a longer word ("200 კვ" is area-ish, not
  // 200×1000) — the lookahead lives INSIDE the optional group so attached currency
  // ("150000ლარი") still matches when no multiplier is present.
  const suf = `(?:(${alt(Object.keys(MONEY_MULTIPLIERS))})(?!\\p{L}))?`
  const budget = alt(PRICE_BUDGET_WORDS)
  const glue = alt(PRICE_BUDGET_GLUE)
  const priceToken = (start, end, v, isMin, curWord, currencySpoken = true) => {
    const currency = curWord && CURRENCY_WORDS.GEL.includes(curWord) ? 'GEL' : 'USD'
    return {
      start, end,
      token: {
        sig: `price:${isMin ? 'min' : 'max'}:${v}`, type: 'price',
        // v24: when no currency was spoken the chip names none — the panel's own currency setting stands
        label: `${isMin ? '≥' : '≤'} ${v.toLocaleString('en-US')}${currencySpoken ? ' ' + currency : ''}`,
        value: { bound: isMin ? 'min' : 'max', amount: v, currency, currencySpoken },  // bare amount = budget = max
      },
    }
  }
  // Pass 1 — numeric range "100k-200k" / "100000-150000$": both bounds in one shape.
  // Marked like everything else (a bare "2-3" is rooms/ids territory, never money).
  // 'd' flag: group indices, so min spans the first number and max the second — the
  // pipeline treats each token's span independently.
  const rangeRe = new RegExp(
    `(${NUM_FRAG})\\s*${suf}\\s*[-–—]\\s*(${NUM_FRAG})\\s*${suf}\\s*(?:${cur})?`, 'giud')
  // v34: a budget word just before or after the range ("budget 100k-200k", "100000-150000 ბიუჯეტი") belongs to it —
  // it marks the numbers as money and is consumed with them instead of being left over as text
  const budgetBefore = new RegExp(`(?:^|\\s)(${budget})(?:[\\s:=-]+(?:${glue}))?[\\s:=-]*$`, 'iu')
  const budgetAfter = new RegExp(`^[\\s-]*(${budget})(?![\\p{L}])`, 'iu')
  // v36 (2026-10-22): a price PER m² ("1500$/მ²", "1500$ per m2", "1000$ კვ.მ", "კვ.მ 1500$") is not a total price.
  // v37 (2026-10-27, filters audit F08): it is the per-m² filter (core 3872) — a single amount becomes a `ppm` token
  // (ppmMin/ppmMax), its unit words consumed with it. A per-m² RANGE is still left unread.
  const areaU = alt(AREA_UNITS.map(w => w.toLowerCase()))
  const perAfter = new RegExp(`^\\s*(?:(?:${alt(PRICE_PER_WORDS)})\\s*)?(?:${areaU})(?!\\p{L})`, 'iu')
  const perBefore = new RegExp(`(?:^|\\s)(?:${areaU})\\s*(?:-?ზე\\s*)?$`, 'iu')
  const unitBelongsToNumber = new RegExp(`\\d\\s*(?:${areaU})\\s*$`, 'iu')
  const perArea = (start, end) => perAfter.test(text.slice(end)) || (perBefore.test(text.slice(0, start)) && !unitBelongsToNumber.test(text.slice(0, start)))
  // the span of a per-m² amount WITH its unit words, so "per m2" is not left over as text
  const perSpan = (start, end) => {
    const a = perAfter.exec(text.slice(end))
    if (a) return { start, end: end + a[0].length }
    const pre = text.slice(0, start)
    const b = perBefore.exec(pre)
    if (b && !unitBelongsToNumber.test(pre)) return { start: b.index + (b[0].length - b[0].trimStart().length), end }
    return null
  }
  // v36: a currency the text does not say is not written (BROWSE_POLICY v24) — "150-200k" / "200k-მდე" wrote USD
  let m
  while ((m = rangeRe.exec(text))) {
    const curWord = (m[5] || '').toLowerCase()
    const bB = budgetBefore.exec(text.slice(0, m.index))
    const bA = budgetAfter.exec(text.slice(m.index + m[0].length))
    const marked = !!(m[2] || m[4] || curWord)
    let lo = amount(m[1], m[2])
    const hi = amount(m[3], m[4])
    // v36 (2026-10-22): the upper number's multiplier scales the lower one when that keeps the range ordered —
    // "150-200k" was 150 … 200,000. "2-200k" stays 2,000 … 200,000 only because 2,000 ≤ 200,000 too.
    if (!m[2] && m[4] && lo != null && hi != null) { const lm = amount(m[1], m[4]); if (lm != null && lm <= hi) lo = lm }
    if (lo == null || hi == null || lo >= hi) continue
    if (perArea(m.index, m.index + m[0].length)) continue
    // …but a budget word does not make "2-3" money: unmarked numbers must still be money-sized (the v24 rule below)
    if ((bB || bA) && (marked || (Number.isInteger(lo) && Number.isInteger(hi) && lo >= 100 && hi <= 10000000))) {
      const loS = bB ? bB.index + bB[0].indexOf(bB[1]) : m.indices[1][0]
      const loE = (m.indices[2] || m.indices[1])[1]
      const end = m.index + m[0].length + (bA ? bA[0].length : 0)
      out.push(priceToken(loS, loE, lo, true, curWord, !!curWord))
      out.push(priceToken(loE, end, hi, false, curWord, !!curWord))
      continue
    }
    // v24 (owner, 2026-09-10): an UNMARKED range whose numbers are money-sized IS money — "500-800" — and the
    // only open question is the currency, which the text cannot answer; the token says so (currencySpoken
    // false) and the policy leaves the panel's currency setting in charge. Money-sized: whole numbers, both
    // ≥ 100 (a bare "2-3" stays rooms/ids territory, engine v6) and ≤ 10,000,000.
    if (!marked && !(Number.isInteger(lo) && Number.isInteger(hi) && lo >= 100 && hi <= 10000000)) continue
    if (!marked && lo >= 1900 && hi <= 2100) continue   // v24: "2023-2024" is a span of YEARS, not a budget
    // Spans: min covers "100k", max covers "-200k…" (hyphen absorbed) — nothing leaks
    // to residual; the pipeline needs the two spans disjoint, not gap-free.
    const loS = m.indices[1][0], loE = (m.indices[2] || m.indices[1])[1], end = m.index + m[0].length
    out.push(priceToken(loS, loE, lo, true, curWord, !!curWord))
    out.push(priceToken(loE, end, hi, false, curWord, !!curWord))
  }
  // Pass 2 — single amount. Engine v6 rule unchanged: a number is money ONLY when it
  // carries a marker — multiplier suffix, currency word, direction word, or (v12) a
  // budget word ("ფასი 200000"). Bare numbers stay residual, where the text search
  // matches them against listing identity (see recognizeListingRef / IntakeTab).
  const re = new RegExp(
    `(?:(${budget})(?:[\\s:=-]+(?:${glue})(?![\\p{L}]))?[\\s:=-]*)?` + // budget word before (ფასი 200000, budget of 150000)
    `(?:(${dirMax})|(${dirMin}))?[\\s-]*` +            // direction before
    `(?:${cur}\\s*)?` +                                // currency before ($200k)
    `(${NUM_FRAG})\\s*${suf}\\s*` +                    // amount + multiplier
    `(?:${cur})?` +                                    // currency after (attached ok)
    `(?:[\\s-]*(?:(${dirMax})|(${dirMin})))?` +      // direction after (200000-მდე)
    // v34: budget word after (200000 budget). `*`, not `+`: the amount's own \s* has already taken the space, and an
    // optional group that fails on `+` simply matches empty — the word was silently left over.
    `(?:[\\s-]*(${budget})(?![\\p{L}]))?`,
    'giu')
  const singles = []
  const perM2 = []
  while ((m = re.exec(text))) {
    if (!m[5]) continue
    // v26: a number glued to a preceding letter/digit ("80m2", "2bed") is not the start of a money amount —
    // it matched "2 under" out of "80m2 under 120k", lost to the area span, and took the direction word
    // with it. Rewind one character so the real "under 120k" is still found.
    const numAt = m.index + m[0].indexOf(m[5])
    if (numAt > 0 && /[\p{L}\p{N}]/u.test(text[numAt - 1]) && !(m[1] || m[2] || m[3] || m[4])) { re.lastIndex = m.index + 1; continue }
    const v = amount(m[5], m[6])
    if (v == null) continue
    const curWord = (m[4] || m[7] || '').toLowerCase()
    const hasDir = !!(m[2] || m[3] || m[8] || m[9])
    // v34: a budget word AFTER a bare number marks it as money only when it is money-sized ("2-3 budget" is not ≤ 3)
    const marked = m[1] || m[6] || curWord || hasDir || (m[10] && v >= 100)
    if (!marked) continue
    if (m[0].trim() === '') continue
    const isMin = !!(m[3] || m[9])                    // any min-direction word
    const per = perSpan(m.index, m.index + m[0].length)
    if (per) { perM2.push({ ...per, v, isMin, curWord }); continue }
    singles.push({ start: m.index, end: m.index + m[0].length, v, raw: amount(m[5], null), mult: m[6] || null, isMin,
      maxOnly: !!(m[2] || m[8]) && !isMin, minOnly: isMin && !(m[2] || m[8]), curWord })
  }
  // v36 (2026-10-22): two amounts side by side with only a joining word between are ONE range — "150-დან 200 ათასამდე",
  // "от 100 до 150 тысяч", "between 100k and 150k" (was ≤ 100k and ≤ 150k, and the draft kept the first). The lower
  // one takes the upper one's multiplier and currency when it has none of its own, and becomes the minimum.
  const JOIN = /^[\s,]*(?:and|to|и|до|და|-|–|—)?\s*$/iu
  const BETWEEN = /(?:^|\s)(between|между|შორის)\s*$/iu
  singles.sort((a, b) => a.start - b.start)
  for (let i = 0; i + 1 < singles.length; i++) {
    const a = singles[i], b = singles[i + 1]
    if (a.maxOnly || b.minOnly || !JOIN.test(text.slice(a.end, b.start))) continue
    let lo = a.v
    if (!a.mult && b.mult) { const lm = amount(String(a.raw), b.mult); if (lm != null && lm <= b.v) lo = lm }
    if (!(lo < b.v)) continue
    const bw = BETWEEN.exec(text.slice(0, a.start))
    Object.assign(a, { v: lo, isMin: true, curWord: a.curWord || b.curWord, start: bw ? bw.index + bw[0].indexOf(bw[1]) : a.start })
    Object.assign(b, { isMin: false, curWord: b.curWord || a.curWord })
    i++
  }
  for (const s of singles) out.push(priceToken(s.start, s.end, s.v, s.isMin, s.curWord, !!s.curWord))
  for (const s of perM2) {
    const currency = s.curWord && CURRENCY_WORDS.GEL.includes(s.curWord) ? 'GEL' : 'USD'
    out.push({
      start: s.start, end: s.end,
      token: {
        sig: `ppm:${s.isMin ? 'min' : 'max'}:${s.v}`, type: 'ppm',
        label: `${s.isMin ? '≥' : '≤'} ${s.v.toLocaleString('en-US')}${s.curWord ? ' ' + currency : ''}/m²`,
        value: { bound: s.isMin ? 'min' : 'max', amount: s.v, currency, currencySpoken: !!s.curWord },
      },
    })
  }
  // Pass 3 — attached lari abbreviation: "200000ლ" (only glued + word-final, see above).
  const lariRe = new RegExp(`(?:^|\\s)(${NUM_FRAG})ლ(?=\\s|$)`, 'giu')
  while ((m = lariRe.exec(text))) {
    const v = amount(m[1], null)
    if (v == null) continue
    const start = m.index + m[0].indexOf(m[1])
    out.push(priceToken(start, m.index + m[0].length, v, false, 'ლარი'))
  }
  return out
}

// v24: a portal name is a source token; the label is the facet's own display name.
function recognizeSource(text) {
  return recognizeWordMap(text, VOCAB.SOURCE_WORDS, 'source')
    .map(x => ({ ...x, token: { ...x.token, label: `🌐 ${VOCAB.SOURCE_LABELS[x.token.value.value] || x.token.value.value}` } }))
}
// Word-list recognizers (deal / kind): whole-word match against vocab forms.
const looseWordMaps = new WeakMap()
function recognizeWordMap(text, map, type) {
  const out = []
  for (const [value, words] of Object.entries(map)) {
    // v32: a digit is a word edge too — "qiravdeba1otakhiani" is two statements glued together
    // v38 (2026-10-28): a Chinese/Japanese word has no spaces around it ("ゴリの土地", "哥里的土地") — it matches
    // anywhere, the way v28 matched place names inside those runs. Every other word keeps its word edges.
    const cjk = words.filter(w => CJK_RE.test(w)), spaced = words.filter(w => !CJK_RE.test(w))
    const res = [
      spaced.length && new RegExp(`(?:^|\\s|(?<=\\d))(${alt(spaced.map(w => w.toLowerCase()))})(?=\\s|$|\\d)`, 'giu'),
      cjk.length && new RegExp(`(${alt(cjk)})`, 'gu'),
    ].filter(Boolean)
    for (const re of res) {
      let m
      while ((m = re.exec(text))) {
        out.push({
          start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
          token: { sig: `${type}:${value}`, type, label: `${type}: ${value}`, value: { value } },
        })
      }
    }
  }
  // v31: a Latin word no listed form matched, read as Georgian letters ("qirith" = ქირით, "qidva" = ყიდვა)
  let loose = looseWordMaps.get(map)
  if (!loose) {
    loose = new Map()
    for (const [value, words] of Object.entries(map)) for (const w of words) {
      if (!/[ა-ჰ]/.test(w) || /\s/.test(w)) continue
      const k = geLoose(w.toLowerCase())
      if (!loose.has(k)) loose.set(k, value)
    }
    looseWordMaps.set(map, loose)
  }
  const wre = /(?:^|\s|(?<=\d))([a-z\u10D0-\u10F0]{3,})(?=\s|$|\d)/g
  let wm
  while ((wm = wre.exec(text))) {
    const start = wm.index + wm[0].indexOf(wm[1]), end = start + wm[1].length
    if (out.some(h => h.start < end && start < h.end)) continue
    const latin = /^[a-z]+$/.test(wm[1])
    const g = latin ? latinGe(wm[1]) : wm[1]
    if (!g) continue
    let value = latin ? loose.get(geLoose(g)) : null
    // v32: a DEAL word typed one letter off ("ქირავდებ", "qirvdeba", "qireit") — the most common word and the most
    // expensive to miss (the whole search lands on the other deal). One edit from exactly one deal, 6+ letters, or nothing.
    if (!value && type === 'deal' && g.length >= 6) {
      const k = geLoose(g)
      const near = new Set()
      for (const [form, v] of loose) if (form.length >= 5 && Math.abs(form.length - k.length) <= 1 && withinOneEdit(k, form)) near.add(v)
      if (near.size === 1) value = [...near][0]
    }
    if (!value) continue
    out.push({ start, end, token: { sig: `${type}:${value}`, type, label: `${type}: ${value}`, value: { value, ...(latin ? {} : { fuzzy: true }) } } })
  }
  return out
}

// v32: two vocabulary words typed with no space — "ბინისყიდვა" (flat + buy), "qiravdebabina". Split only where BOTH
// halves are whole listed forms of a deal, kind or subtype; anything else stays one unread word.
const GLUED_MAPS = () => [[DEAL_WORDS, 'deal'], [SUBTYPE_WORDS, 'subtype'], [KIND_WORDS, 'kind']]
let gluedForms_ = null
function gluedForms() {
  if (gluedForms_) return gluedForms_
  gluedForms_ = new Map()
  for (const [map, type] of GLUED_MAPS()) for (const [value, words] of Object.entries(map)) for (const w of words) {
    if (/\s/.test(w)) continue
    const lw = w.toLowerCase()
    for (const k of [lw, /[ა-ჰ]/.test(lw) ? geLoose(lw) : null]) if (k && k.length >= 3 && !gluedForms_.has(k)) gluedForms_.set(k, { type, value })
  }
  return gluedForms_
}
function recognizeGlued(text) {
  const out = []
  const forms = gluedForms()
  const look = w => forms.get(w) || (/^[a-z]+$/.test(w) ? (latinGe(w) && forms.get(geLoose(latinGe(w)))) : null)
  const wre = /(?:^|\s)([a-z\u10D0-\u10F0]{7,})(?=\s|$)/g
  let m
  while ((m = wre.exec(text))) {
    const w = m[1], at = m.index + m[0].indexOf(w)
    if (look(w)) continue
    for (let i = 3; i <= w.length - 3; i++) {
      const a = look(w.slice(0, i)), b = look(w.slice(i))
      if (!a || !b || a.type === b.type) continue
      for (const [x, s, e] of [[a, at, at + i], [b, at + i, at + w.length]])
        out.push({ start: s, end: e, token: { sig: `${x.type}:${x.value}`, type: x.type, label: `${x.type}: ${x.value}`, value: { value: x.value } } })
      break
    }
  }
  return out
}

// Districts: every query word, stemmed, against the stemmed DB district names
// (name_ka + name_en, split on -/spaces so "გლდანი-ნაძალადევი" matches by half)
// plus the hand-kept alias map. Stemmer confirms, never raw substring — Georgian
// names inflect (ვაკეში ≠ ვაკე as strings).
// Typo folding (engine v3, 2026-07-06). Georgian QWERTY shift-typos are SYSTEMATIC —
// dropping Shift turns თ→ტ, ღ→რ, ჩ→ც, ძ→ზ, ჭ→წ, შ→ს, ჟ→ჯ ("ორთაჭალა" typed lazily is
// "ორტაწალა"). Phonetic slips ღ→გ ("ჩუგურეთი") and ქ→კ ("თემკა") are near as regular.
// Folding BOTH dictionary and query through these maps makes every such typo — present
// and future — match generatively, no enumerated typo lists. Deterministic: a folded
// form that collides across two districts is POISONED (dropped), never guessed.
const KB_FOLD = { 'თ': 'ტ', 'ღ': 'რ', 'ჩ': 'ც', 'ძ': 'ზ', 'ჭ': 'წ', 'შ': 'ს', 'ჟ': 'ჯ' }
const PH_FOLD = { 'ღ': 'გ', 'ქ': 'კ', 'თ': 'ტ', 'ძ': 'ზ', 'ჩ': 'ც', 'ჭ': 'წ', 'შ': 'ს' }
const foldWith = (s, map) => [...s].map(ch => map[ch] || ch).join('')
// Generic location-type words that must NEVER unique-prefix a district (engine v10). "მეტრო"
// (subway) is a 5-char prefix of the real district "მეტრომშენი" (Metromsheni), so a bare
// "მეტრო" — which an agent almost always means as "near the metro", not that district — used to
// compile to 📍 Metromsheni (found in the golden eval, "მეტრო დელისთან …"). Blocked at the PREFIX
// step only: the full "მეტრომშენი" still matches EXACTLY, and "მეტრო" is left for the
// near-location recognizer / residual. Stored stemmed, matched against the query stem.
let GENERIC_ADMIN_STEMS_ = null
let TYPE_WORD_STEMS_ = null
const TYPE_WORD_STEMS = () => (TYPE_WORD_STEMS_ ??= new Set([...Object.values(KIND_WORDS), ...Object.values(SUBTYPE_WORDS)].flat().filter(w => !/\s/.test(w)).map(w => georgianStem(w.toLowerCase()))))
const GENERIC_ADMIN_STEMS = { has: st => (GENERIC_ADMIN_STEMS_ ??= new Set([...GENERIC_ADMIN_WORDS].map(w => georgianStem(w)))).has(st) }
const METRO_MARKER_WORDS = ['მეტრო', 'metro', 'метро', 'مترو', '地下鉄', '地铁', 'u-bahn', 'metrosu']   // v30: the site's other languages
const DISTRICT_PREFIX_STOPWORDS = new Set(METRO_MARKER_WORDS.map(w => georgianStem(w)))
// Generic administrative words that mark a COMPOUND official name (a settlement / municipality /
// microdistrict / village). Such names embed street & person names — "ილია ჭავჭავაძის დასახლება",
// "შოთა რუსთაველის დასახლება", "დავით აღმაშენებელის დასახლება" (all Rustavi settlements) — so
// indexing their word-PARTS made a bare Tbilisi street ("ჭავჭავაძის"/"რუსთაველი"/"აღმაშენებელი")
// falsely resolve to a far settlement. A name containing one of these is NOT part-indexed (engine
// v10); it stays findable via address text search, and its simple parent ("გლდანი") is still
// indexed from that district's own single-word row.
// Engine v11 (2026-07-18): common-word collision guard. The 26-batch bug hunt proved that
// part-indexing every word of compound names + prefix/stem-matching turns EVERYDAY words into
// wrong district chips (ვერ→Vera, ვეძებ→Vedzisi, დიდი→Didi Digomi, ძველი/თბილისი→Old Tbilisi,
// მასივი/პლატო→one arbitrary massif/plateau, მეტრი→Metromsheni, ბაღი→fold→Bagebi, ეზო→POI) —
// a natural pasted client message gained TWO garbage districts. Three-part fix:
//  1. compound district names/aliases now ALSO index as adjacent-stem PHRASES, matched first
//     ("ძველი თბილისი", "დიდი დიღომი", "დიღმის მასივი" typed in full still resolve);
//  2. GENERIC_PLACE_WORDS (vocab v7) are blocked at STEM level in the single-word pass — a
//     generic word alone never resolves; its compound stays reachable via the phrase pass and
//     its distinctive part (დიღომი, ნუცუბიძე);
//  3. COMMON_WORD_HOMONYMS are blocked as EXACT words only (ვერ/ვეძებ share the district's own
//     stem — stem-blocking would kill ვერა/ვეძისი; inflected place forms still resolve).
// Same stem-block applies to single-word near-location gazetteer entries (ეზო) and to the
// suggestDistricts completion hints (a hint must never insert a word the compiler won't match).
const GENERIC_ADMIN_WORDS = new Set(
  ['დასახლება', 'მუნიციპალიტეტი', 'მიკრორაიონი', 'რაიონი', 'სოფელი', 'district', 'municipality', 'microdistrict', 'settlement', 'village',
   // v28: the Russian names are indexed too, and their compound official forms skip the same way
   'посёлок', 'поселок', 'микрорайон', 'район', 'село', 'муниципалитет', 'деревня', 'массив',
   // v30: the same words in the other site languages (district_name_i18n), so their compound names behave alike
   'siedlung', 'dorf', 'mikrobezirk', 'gemeinde', 'köyü', 'köy', 'yerleşimi', 'yerleşim', 'mikrorayonu', 'mikrorayon', 'belediyesi',
   'قرية', 'مستوطنة', 'ميكرورايون', 'بلدية', 'حي']
    .flatMap(w => [w, georgianStem(w)]))
// Engine v11 collision guard (see the version-note above): stems that never resolve alone, and
// exact everyday words whose stem is a real district's own stem. Data lives in searchVocab.js.
const CJK_RE = /[\p{sc=Han}\p{sc=Hiragana}\p{sc=Katakana}]/u
const WORD_RE = /[\p{sc=Han}\p{sc=Hiragana}\p{sc=Katakana}ー・･]+|(?:(?![\p{sc=Han}\p{sc=Hiragana}\p{sc=Katakana}ー・･])[\p{L}\p{N}.])+/gu
const ORDINAL_PART = /^(?:\d+(?:st|nd|rd|th|-?[a-zა-ჰ])?|[ivx]+|მე|ლი|\d+-?(?:ლი|ე|й|я|го|ый|ой|ий))$/iu
const LATIN_ONLY = /^[\p{sc=Latin}\p{N}\s.,'’()\-]+$/u
const GENERIC_PLACE_STEMS = new Set(GENERIC_PLACE_WORDS.map(w => georgianStem(w.toLowerCase())))
const HOMONYM_WORDS = new Set(COMMON_WORD_HOMONYMS.map(w => w.toLowerCase()))

// v26: the Russian ЙЦУКЕН layout typed on a Latin keyboard ("cfgehnfkj" → "сабуртало")
const LATIN_TO_CYRILLIC_LAYOUT = Object.fromEntries([...'qwertyuiop[]asdfghjkl;\'zxcvbnm,.'].map((c, i) => [c, [...'йцукенгшщзхъфывапролджэячсмитьбю'][i]]))
// Damerau distance ≤ 1 (one insert, delete, substitute or adjacent swap)
function withinOneEdit(a, b) {
  if (a === b) return true
  const la = a.length, lb = b.length
  if (Math.abs(la - lb) > 1) return false
  if (la === lb) {
    const d = []; for (let i = 0; i < la; i++) if (a[i] !== b[i]) d.push(i)
    return d.length === 1 || (d.length === 2 && d[1] === d[0] + 1 && a[d[0]] === b[d[1]] && a[d[1]] === b[d[0]])
  }
  const [s, l] = la < lb ? [a, b] : [b, a]
  let i = 0, j = 0, skipped = false
  while (i < s.length && j < l.length) { if (s[i] === l[j]) { i++; j++ } else { if (skipped) return false; skipped = true; j++ } }
  return true
}
// v30: the district index is built ONCE per dictionary (and per public-mode locale / home city) and reused — it grew
// to 8 languages, whole-name phrases and numbered sub-areas, and rebuilding it for every query cost 80–150 ms of CPU.
const districtIndexCache = new WeakMap()
function districtIndexFor(districts, opts) {
  const key = `${opts.publicMode ? 1 : 0}|${opts.locale || ''}|${opts.homeCity ? String(opts.homeCity).toLowerCase() : ''}`
  let byKey = districtIndexCache.get(districts)
  if (!byKey) { byKey = new Map(); districtIndexCache.set(districts, byKey) }
  let ix = byKey.get(key)
  if (!ix) { ix = buildDistrictIndex(districts, opts); byKey.set(key, ix) }
  return ix
}
function buildDistrictIndex(districts, opts) {
  const index = new Map()     // stemmed form -> { code, display } (exact dictionary forms)
  const folded = new Map()    // folded stemmed form -> { code, display } | null (null = poisoned)
  const addFolded = (key, val) => {
    const cur = folded.get(key)
    if (cur === undefined) folded.set(key, val)
    else if (cur && cur.code !== val.code) folded.set(key, null)  // ambiguous across districts — never guess
  }
  // Tbilisi-primary (engine v10): the Desk is Tbilisi-first, but seed data has same-named
  // districts in other cities (Batumi's "რუსთაველი"/"აღმაშენებელი" neighborhoods = famous Tbilisi
  // AVENUES; "ბათუმი" itself). A bare word that resolves ONLY to a non-Tbilisi district is far more
  // likely a Tbilisi street/place the agent typed, so we DON'T hard-filter to the far district —
  // it stays residual and address text search decides (which also finds the far-city case). Tbilisi
  // = TBS_/PERI_ (peri-urban satellites kept). On a stem shared by a Tbilisi AND a non-Tbilisi
  // district, the Tbilisi one wins so it's never wrongly suppressed.
  const isTbilisi = code => code.startsWith('TBS_') || code.startsWith('PERI_')
  // Engine v16: CITY-level rows (MUN_/TWN_ — plain city names like ბათუმი/ქუთაისი/გორი)
  // resolve too; the Tbilisi-primary suppression keeps blocking only far-city
  // NEIGHBORHOODS (BAT_/KUT_ children — Batumi's რუსთაველი etc.), which is the actual
  // hijack risk the rule was built for.
  const isCity = code => code.startsWith('MUN_') || code.startsWith('TWN_')
  // v28 (public mode): the visitor's own city — a neighbourhood of the city being searched ("gonio" on a Batumi search)
  // is exactly what they mean, so the Tbilisi-primary suppression does not apply to it. The Desk passes no home city.
  const home = opts.homeCity ? String(opts.homeCity).toLowerCase() : null
  const cityOf = home ? new Map(districts.map(d => [d.code, String(d.city || '').toLowerCase()])) : null
  const resolvable = code => isTbilisi(code) || isCity(code) || (!!home && cityOf.get(code) === home)
  // v26: which districts claim a stem only as PART of a longer name (for the family rule below)
  const partCodes = new Map()   // stem -> Map(code -> the part as written, for the chip)
  const exactWhole = new Map()  // v29: exact whole single-word name → { code } (null when two districts share it)
  const cjkNames = []           // v28: Chinese/Japanese names, matched as substrings (no spaces between words)
  const add = (part, code, display, whole = false) => {
    if (part.length < 3 && !CJK_RE.test(part)) return
    // v29: a number or an ordinal is never a place on its own — "3rd" matched Vazha-Pshavela 3rd Quarter, so "Gldani 3rd
    // Microdistrict" searched Gldani AND that quarter; "მე-3" pulled a family of Zghvisubani blocks. It still counts
    // inside a whole name (the phrase index below). (DIGHOMI.md: a number without its parent type never resolves.)
    if (ORDINAL_PART.test(part)) return
    const stem = georgianStem(part)
    if (CJK_RE.test(part)) {
      // same rule as the phrases: two rows under one name are both, never whichever came first
      const prev = cjkNames.find(n => n.name === part)
      // v38 (2026-10-28): …except a town and its same-named municipality — the town wins, as in the word index below.
      // "ゴリ" / "哥里" searched TWN_GORI + MUN_GORI while every other language searched the town.
      const twinOf = (a, b) => a.startsWith('TWN_') && b === 'MUN_' + a.slice(4)
      if (!prev) cjkNames.push({ name: part, code, display })
      else if (twinOf(prev.code, code) && !prev.codes) { /* keep the town */ }
      else if (twinOf(code, prev.code) && !prev.codes) { prev.code = code; prev.display = display }
      else if (prev.code !== code && !(prev.codes || []).includes(code)) { prev.codes = [...new Set([...(prev.codes || [prev.code]), code])].sort(); prev.code = prev.codes[0] }
    }
    if (!whole && isTbilisi(code) && /^\p{L}{5,}$/u.test(part)) {
      if (!partCodes.has(stem)) partCodes.set(stem, new Map())
      partCodes.get(stem).set(code, part)
    }
    // v29: the exact written name, before stemming — თხინვალა (Nutsubidze) and თხინვალი (Vake) are two places whose
    // stems are one; a word typed exactly as a whole name is that place
    if (whole) { const ex = exactWhole.get(part); if (ex === undefined) exactWhole.set(part, { code, display, whole: true }); else if (ex && ex.code !== code) exactWhole.set(part, null) }
    const cur = index.get(stem)
    // v26: a district whose WHOLE name is this word owns it; a longer name merely containing it never
    // takes it away ("metromsheni" is TBS_METROMSHENI, not "Zemo Avlabari, Metromsheni" — before this,
    // whichever name was indexed LAST won, i.e. alphabetical order decided).
    if (cur && cur.whole && !whole && !(resolvable(code) && !resolvable(cur.code))) return
    if (cur && whole && !cur.whole && !(resolvable(cur.code) && !resolvable(code))) { index.set(stem, { code, display, whole }); return }
    // Never demote on a shared stem: a RESOLVABLE code (Tbilisi or city) is never
    // overwritten by a suppressed one (Batumi's industrial-district row must not shadow
    // the ბათუმი city row), Tbilisi beats non-Tbilisi (PERI_RUSTAVI over MUN_RUSTAVI),
    // and a town beats its same-named municipality (TWN_GORI over MUN_GORI).
    if (!(cur && ((resolvable(cur.code) && !resolvable(code))
                  || (isTbilisi(cur.code) && !isTbilisi(code))
                  || (cur.code.startsWith('TWN_') && code.startsWith('MUN_'))))) index.set(stem, { code, display, whole })
    for (const map of [KB_FOLD, PH_FOLD]) {
      const f = foldWith(stem, map)
      if (f !== stem) addFolded(f, { code, display })
    }
  }
  // Phrase index (engine v11): every multi-word name/alias also indexes as its adjacent stem
  // SEQUENCE, matched before single words — so "ძველი თბილისი"/"დიდი დიღომი"/"დიღმის მასივი"
  // typed in full resolve even though their generic parts are stem-blocked below.
  const phrases = []   // { stems, code, display } — longest first at match time
  const phraseOwner = new Map()   // v29: stems key → code, or null when two districts carry the same whole name (never guess)
  const addPhrase = (name, code, display) => {
    // v29: the phrase is tokenised EXACTLY as the query is (same word pattern, numbers and one-letter words kept), so a
    // whole official name — "გლდანის მე-3 მიკრორაიონი", "Didi Dighomi 1st Microdistrict" — matches as typed
    const stems = (String(name).replace(/İ/g, 'I').toLowerCase().match(WORD_RE) || []).map(w => w.replace(/\.+$/, '')).filter(Boolean).map(georgianStem)
    if (stems.length < 2) return
    const key = stems.join(' ')
    // v29: two district ROWS carrying the same whole name ("Dighomi Village" = TBS_DIGHOMI_VILLAGE and TBS_DIGHMIS_SOPELI)
    // are one place split across two codes — the name means BOTH (the panel's OR), not neither
    const prev = phrases.find(p => p.key === key)
    if (prev) { if (prev.code !== code && !(prev.codes || []).includes(code)) { prev.codes = [...new Set([...(prev.codes || [prev.code]), code])].sort(); prev.code = prev.codes[0] } return }
    phraseOwner.set(key, code)
    phrases.push({ stems, code, display, key })
  }
  const addName = (name, code, display, phraseOnly = false) => {
    const parts = name.replace(/İ/g, 'I').toLowerCase().split(/[\s,-]+/).filter(Boolean)
    if (!phraseOnly || parts.length === 1) for (const part of parts) add(part, code, display, parts.length === 1)
    if (parts.length >= 2 || (String(name).match(WORD_RE) || []).length >= 2) addPhrase(name, code, display)
    // "Didgori (neighbourhood)": the bracketed qualifier is ours, not something people type
    const bare = name.replace(/\s*\([^)]*\)\s*/g, ' ').trim()
    if (bare && bare !== name.trim()) addName(bare, code, display, phraseOnly)
  }
  for (const d of districts) {
    // v28: name_ru too — every row carries one and none was indexed, so Russian worked only where a hand alias existed
    for (const name of [d.name_ka, d.name_en, d.name_ru].filter(Boolean)) {
      const parts = name.toLowerCase().split(/[\s-]+/)
      // Compound official names (settlement/municipality/microdistrict/…): their PARTS are street/person names that
      // collide with real Tbilisi streets (see GENERIC_ADMIN_WORDS), so they are never indexed word by word — but the
      // WHOLE name is exact and matches as a phrase (v29; until then "Gldani 3rd Microdistrict" could not be typed).
      const compound = parts.some(p => GENERIC_ADMIN_WORDS.has(p) || GENERIC_ADMIN_WORDS.has(georgianStem(p)))
      addName(name, d.code, d.name_en || d.name_ka, compound)
    }
  }
  // v26: the DB's own district.aliases (171 of 366 rows: latin/cyrillic/colloquial forms, "old town") — the
  // canonical vocabulary, read when the caller sends it. A multi-word alias matches only as a whole PHRASE:
  // its parts are often a street ("ვაჟა ფშაველას კვარტლები" must not make "ფშაველას" mean Saburtalo).
  for (const d of districts) {
    for (const a of Array.isArray(d.aliases) ? d.aliases : []) if (a && String(a).trim().length >= 3) addName(String(a), d.code, d.name_en || d.name_ka, true)
  }
  // v29 (public mode): every site language's name for the district (district_name_i18n via the gazetteer's `names`,
  // owner-reviewed, 366 × 8). A non-Latin script (ar ja zh ru ka) cannot be mistaken for an English word and always
  // counts; a LATIN name from another locale counts only for a visitor in that locale — German "Wake" is Vake, and
  // "wake" is also English. Multi-word names match whole, never word by word (same rule as the DB aliases).
  if (opts.publicMode) {
    for (const d of districts) {
      for (const [loc, name] of Object.entries(d.names || {})) {
        if (!name) continue
        // English is every visitor's fallback language, so its (reviewed) name always counts; a German/Turkish Latin name
        // only for that locale's visitor
        if (LATIN_ONLY.test(name) && loc !== 'en' && loc !== opts.locale) continue
        addName(String(name), d.code, d.name_en || d.name_ka, true)
      }
    }
  }
  for (const [code, aliases] of Object.entries(DISTRICT_ALIASES)) {
    const d = districts.find(x => x.code === code)
    if (!d) continue        // alias for a district this deployment doesn't have — skip
    for (const a of aliases) addName(a, code, d.name_en || d.name_ka)
  }
  phrases.sort((a, b) => b.stems.length - a.stems.length)
  // a same-name pair must be resolvable on both sides to be offered as one (a far-city twin never joins a Tbilisi name)
  for (const p of phrases) if (p.codes) { p.codes = p.codes.filter(resolvable); if (p.codes.length < 2) { p.code = p.codes[0] || p.code; delete p.codes } }
  // v26: a word that no district is NAMED, but that several Tbilisi districts carry as part of their
  // names ("dighomi": Didi Dighomi, Dighomi 1-9, Dighomi Village, Dighomi Massive ×6 …), is the FAMILY,
  // not whichever of them was indexed last. It resolves to all of them (OR, the panel's own semantics
  // for several ticked districts). A word with a whole-name owner never becomes a family.
  for (const [stem, byCode] of partCodes) {
    const cur = index.get(stem)
    if (!cur || cur.whole || byCode.size < 2 || GENERIC_PLACE_STEMS.has(stem) || DISTRICT_PREFIX_STOPWORDS.has(stem)) continue
    const codes = [...byCode.keys()].sort()
    const word = [...byCode.values()][0]
    index.set(stem, { code: codes[0], codes, display: word.charAt(0).toUpperCase() + word.slice(1) + ' ·' + codes.length, family: true })
  }
  // Query-side fold lookup: try the token folded through each map against BOTH the exact
  // index (typo in query, clean dictionary) — the common case — and the folded index
  // (handles dictionary parts that themselves contain foldable letters).
  const foldHit = (stem) => {
    for (const map of [KB_FOLD, PH_FOLD]) {
      const f = foldWith(stem, map)
      const hit = index.get(f) ?? folded.get(f)
      if (hit) return hit           // poisoned entries are null → skipped
    }
    return null
  }
  // Unique-prefix fallback (engine v2, 2026-07-06): "ვარკე"/"varke" typed mid-word
  // compiled to nothing and fell into a text search that found nothing (cards say
  // "ისანი-სამგორი", not ვარკეთილი). A token ≥4 chars that is a prefix of exactly ONE
  // district's stems (unique by CODE) is still a deterministic recognition — no fuzzy
  // scoring. An ambiguous prefix ("დიდ…" → დიდუბე/დიდი დიღომი) stays residual and is
  // logged, per the unknown≠guess rule.
  // Latin transliterated postpositions (engine v13) — the latin twins of the stemmer's
  // Georgian POSTPOSITIONS list, longest first. Exact-index retry only.
  const LATIN_POSTPOSITIONS = ['istvis', 'tvis', 'amde', 'mde', 'dan', 'ken', 'tan', 'vit', 'gan', 'ze', 'shi', 'si']
  const latinHit = (raw) => {
    if (!/^[a-z]+$/.test(raw)) return null
    for (const suf of LATIN_POSTPOSITIONS) {
      if (raw.endsWith(suf) && raw.length - suf.length >= 3) {
        const base = raw.slice(0, -suf.length)
        // The genitive elides the final i ("gldani"+"shi" → "gldanshi") — retry with it.
        const hit = index.get(base) || index.get(base + 'i')
        if (hit) return hit
      }
    }
    // v31: read as Georgian letters and stemmed like a Georgian word — "qutaisshi" = ქუთაისში → ქუთაისი, which the
    // Latin postposition strip cannot reach because the district's Latin name is spelled "Kutaisi"
    // only when a case ending really came off: "list" = ლისთ loses one letter and would otherwise be Lisi
    const g = raw.length >= 4 ? latinGe(raw) : null
    if (g) { const st = georgianStem(g); if (st.length >= 3 && g.length - st.length >= 2) { const h = index.get(st) || foldHit(st); if (h) return h } }
    return null
  }
  const prefixHit = (stem) => {
    if (stem.length < 4 || DISTRICT_PREFIX_STOPWORDS.has(stem)) return null
    let hit = null
    for (const [key, val] of index) {
      if (!key.startsWith(stem)) continue
      if (hit && hit.code !== val.code) return null   // two different districts — ambiguous
      hit = val
    }
    return hit
  }
  const fuzzyHit = (raw) => {
    const lay = [...raw].map(c => LATIN_TO_CYRILLIC_LAYOUT[c] || c).join('')
    if (lay !== raw) { const h = index.get(georgianStem(lay)); if (h) return { ...h, fuzzy: true } }
    if (raw.length < 5 || !/^[a-zа-яё]+$/u.test(raw)) return null
    let found = null
    for (const [key, val] of index) {
      if (!val.whole || !resolvable(val.code) || Math.abs(key.length - raw.length) > 1 || !withinOneEdit(raw, key)) continue
      if (found && found.code !== val.code) return null   // two different districts — ambiguous, never guess
      found = val
    }
    return found ? { ...found, fuzzy: true } : null
  }
  return { resolvable, index, phrases, exactWhole, cjkNames, foldHit, latinHit, prefixHit, fuzzyHit }
}

function recognizeDistrict(text, districts, opts = {}) {
  if (!districts?.length) return []
  const { resolvable, index, phrases, exactWhole, cjkNames, foldHit, latinHit, prefixHit, fuzzyHit } = districtIndexFor(districts, opts)
  const out = []
  const pushHit = (start, end, hit) => out.push({
    start, end,
    token: { sig: hit.codes ? `district:family:${hit.codes.join('+')}` : `district:${hit.code}`, type: 'district', label: `📍 ${hit.display}`,
             value: { code: hit.code, display: hit.display, ...(hit.codes ? { codes: hit.codes } : {}), ...(hit.fuzzy ? { fuzzy: true } : {}) } },
  })
  // Tokenize once with positions (stems computed per word).
  const words = []
  // v28: a Chinese/Japanese run is its own word (those scripts do not space words from the digits and words around them)
  const wordRe = new RegExp(WORD_RE.source, 'gu')
  let m
  while ((m = wordRe.exec(text))) {
    // Trailing periods are sentence punctuation, not part of the name ("ვაკე. საბურთალო"
    // missed Vake before v14); interior dots stay — they're cadastral/decimal shapes.
    const raw = m[0].toLowerCase().replace(/\.+$/, '')
    if (!raw) continue
    const g = latinGe(raw)   // v31: a Latin-typed Georgian word also carries its Georgian stem, for the phrase pass
    words.push({ start: m.index, end: m.index + raw.length, raw, stem: georgianStem(raw), gstem: g ? geLoose(georgianStem(g)) : null })
  }
  const used = new Array(words.length).fill(false)
  // Pass 1 — adjacent-stem phrases, longest first (exact stems only: deterministic, no folds).
  for (const p of phrases) {
    if (!resolvable(p.code)) continue   // Tbilisi-primary (+cities, v16) applies to phrases too
    // v30: the early pass takes only names of 3+ words ("Vazha-Pshavela Avenue 1st Block", "Tbilisi Sea Area"); a 2-word
    // district name inside a longer complex name ("Didi Dighomi Inn") is left for the location recognizer
    // (or 2 words when one is an area-type word — "Hualing Settlement" must beat the Hualing COMPLEX; a name with a type
    // word cannot be the start of a complex's name)
    // v31: …or a word the type recognizers would claim — "მუხიანის აგარაკები" / "Mukhiani Cottages" is the place, not a
    // country house in Mukhiani, now that plural type words are vocabulary
    if (opts.phrasesOnly && p.stems.length < 3 && !p.stems.some(st => GENERIC_ADMIN_STEMS.has(st) || TYPE_WORD_STEMS().has(st))) continue
    const L = p.stems.length
    for (let i = 0; i + L <= words.length; i++) {
      let ok = true
      for (let k = 0; k < L; k++) if (used[i + k] || (words[i + k].stem !== p.stems[k] && !(L > 1 && words[i + k].gstem && /[ა-ჰ]/.test(p.stems[k]) && words[i + k].gstem === geLoose(p.stems[k])))) { ok = false; break }
      if (!ok) continue
      for (let k = 0; k < L; k++) used[i + k] = true
      pushHit(words[i].start, words[i + L - 1].end, p)
    }
  }
  if (opts.phrasesOnly) { out.sort((a, b) => a.start - b.start); return out }   // (Pass 1 ran with minPhrase — see below)
  // Pass 1b (v28) — a Chinese/Japanese name inside a run of those scripts ("巴统两卧室"), longest name first
  if (cjkNames.length) {
    cjkNames.sort((a, b) => b.name.length - a.name.length)
    for (let i = 0; i < words.length; i++) {
      const w = words[i]
      if (used[i] || !CJK_RE.test(w.raw)) continue
      const hit = cjkNames.find(n => w.raw.includes(n.name) && resolvable(n.code))
      if (!hit) continue
      const at = w.start + w.raw.indexOf(hit.name)
      used[i] = true
      const codes = (hit.codes || []).filter(resolvable)
      pushHit(at, at + hit.name.length, codes.length > 1 ? { code: codes[0], codes, display: hit.display } : { code: hit.code, display: hit.display })
    }
  }
  // Pass 2 — single words, with the v11 collision guard: generic place words (by stem) and
  // everyday homonyms (by exact word) never resolve alone.
  for (let i = 0; i < words.length; i++) {
    const w = words[i]
    if (used[i]) continue
    if (GENERIC_PLACE_STEMS.has(w.stem) || HOMONYM_WORDS.has(w.raw)) continue
    if (GENERIC_ADMIN_WORDS.has(w.raw) || GENERIC_ADMIN_WORDS.has(w.stem)) continue   // v29: "settlement" alone names no place
    // Precedence: exact dictionary form → fold-typo match → unique prefix. Folds beat
    // prefixes so a full-but-mistyped word resolves as itself, not as a prefix accident.
    // Engine v13 latin fallback: georgianStem is a no-op on latin, so "vakeshi" never
    // matched the "vake" alias — on a miss, strip ONE transliterated postposition and
    // retry the exact index only (deterministic, no folds/prefixes on the stripped form).
    const ex = exactWhole.get(w.raw)
    let hit = (ex && resolvable(ex.code) ? ex : null) || index.get(w.stem) || foldHit(w.stem) || prefixHit(w.stem) || latinHit(w.raw)
    // v30: a Latin genitive of a whole name ("gldanis" = Gldani's) beats a PART of a longer name ("Gldanis Sopeli")
    if (hit && !hit.whole && /^[a-z]{4,}is$/.test(w.raw)) { const g = exactWhole.get(w.raw.slice(0, -1)); if (g && resolvable(g.code)) hit = g }
    // v26 (public mode): a visitor's typo — a wrong keyboard layout, or ONE edit (insert/delete/substitute/
    // swap) from a WHOLE district name. Unique-or-nothing, like the prefix rule; the token says it was fuzzy
    // (confidence medium, shown as a removable chip). The Desk keeps its v3 folds only.
    if (!hit && opts.publicMode) hit = fuzzyHit(w.raw)
    // v32: "ბინის ყიდვა ყაზბეგზე" — ON Kazbegi is the avenue; a far city/municipality is IN (ყაზბეგში). A city-level place
    // read from a word ending in -ზე / -ze is left as text, which the street lookup then answers.
    if (hit && /^(MUN_|TWN_)/.test(hit.code) && /(ზე|ze)$/.test(w.raw)) hit = null
    // Tbilisi-primary (+city rows, v16): far-city neighborhoods stay residual (see add).
    if (hit && resolvable(hit.code)) pushHit(w.start, w.end, hit)
  }
  out.sort((a, b) => a.start - b.start)
  return out
}

// ── numbered sub-areas (engine v30) ──────────────────────────────────────────
// "გლდანის მე-3 მ/რ" / "Глдани 3 мкр" / "gldani 3rd mikro" → Gldani 3rd Microdistrict. The children come from the district
// rows' own reviewed English names ("Gldani 3rd Microdistrict", "Varketili 3rd Massif, 1st Block"): base name + (number,
// type) pairs, the base resolved to its own district (or the row's parent when the base is the parent's name plus a
// generic word, "Lilo Settlement"). The query side needs a district token next to the pairs, and the TYPE WORD is
// required — a bare number never picks a child (DIGHOMI.md).
const AREA_TYPE_CLASS = new Map(Object.entries(AREA_TYPE_WORDS).flatMap(([cls, ws]) => ws.map(w => [w.toLowerCase(), cls])))
const AREA_TYPE_ALT = alt([...AREA_TYPE_CLASS.keys()])
const ROMAN = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10 }
const AREA_NUM = String.raw`(?:მე-?\s*)?\d+(?:\s*-?\s*(?:ლი|ე|st|nd|rd|th|й|я|ый|ой|ий|го|ая))?(?:\s*-?\s*[aაbბ](?![\p{L}]))?|(?:x|ix|iv|v?i{1,3}|v|vi{0,3})(?![\p{L}])|[„"«']?[aა][“"»']?(?=\s)`
const AREA_TYPE = String.raw`(?:${AREA_TYPE_ALT})(?:-?(?:ში|ის|ზე))?`
const AREA_PAIR = String.raw`(?:(?:${AREA_NUM})\s*(?:${AREA_TYPE})|(?:${AREA_TYPE})\s*(?:${AREA_NUM}))`
const AREA_AFTER = new RegExp(String.raw`^(?:\s*[,\-–]?\s*${AREA_PAIR})+(?=$|[\s,.;!?])`, 'iu')
const AREA_BEFORE = new RegExp(String.raw`(?:^|\s)((?:${AREA_PAIR}\s*[,\-–]?\s*)+)$`, 'iu')
const AREA_PAIR_G = String.raw`(${AREA_NUM})\s*(${AREA_TYPE})|(${AREA_TYPE})\s*(${AREA_NUM})`
const areaNum = raw => {
  let t = String(raw).toLowerCase().replace(/^მე-?\s*/, '').replace(/[„"«»“'\s-]/g, '')
  if (ROMAN[t] != null) return String(ROMAN[t])
  t = t.replace(/(?:ლი|ე|st|nd|rd|th|й|я|ый|ой|ий|го|ая)$/, '').replace(/ა/g, 'a').replace(/ბ/g, 'b')
  return t
}
const areaType = raw => {
  const w = String(raw).toLowerCase()
  const hit = [...AREA_TYPE_CLASS.keys()].filter(k => w.startsWith(k)).sort((a, b) => b.length - a.length)[0]
  return hit ? AREA_TYPE_CLASS.get(hit) : null
}
function areaPairs(text) {
  const out = [], re = new RegExp(AREA_PAIR_G, 'giu')
  let m
  while ((m = re.exec(text))) {
    const num = areaNum(m[1] ?? m[4]), type = areaType(m[2] ?? m[3])
    if (num && type) out.push(num + type)
  }
  return out
}
const numberedCache = new WeakMap()
function numberedChildren(districts) {
  if (numberedCache.has(districts)) return numberedCache.get(districts)
  const byEn = new Map()
  for (const d of districts) for (const n of [d.names?.en, d.name_en]) if (n) byEn.set(n.toLowerCase().trim(), d.code)
  const EN_PAIR = /(?:(\d+)(?:st|nd|rd|th)?([A-Z])?|"([A-Z])")\s+(Microdistrict|Micro District|Block|Quarter|Massif|Massive)\b/g
  const kids = new Map()
  for (const d of districts) {
    const en = d.names?.en || d.name_en
    if (!en || !EN_PAIR.test(en)) continue
    EN_PAIR.lastIndex = 0
    const pairs = [...en.matchAll(EN_PAIR)].map(m => (m[3] ? m[3].toLowerCase() : m[1] + (m[2] || '').toLowerCase()) + areaType(m[4].toLowerCase()))
    const base = en.replace(EN_PAIR, ' ').replace(/[,\s–-]+$/g, '').replace(/\s*,\s*/g, ' ').trim().toLowerCase()
    const parent = districts.find(x => x.code === d.parent_code)
    const parentEn = (parent?.names?.en || parent?.name_en || '').toLowerCase()
    const baseCode = byEn.get(base) || (parentEn && base.startsWith(parentEn) ? parent.code : null)
    if (!baseCode || baseCode === d.code) continue
    const key = baseCode + '|' + pairs.join(',')
    kids.set(key, kids.has(key) && kids.get(key).code !== d.code ? null : { code: d.code, display: d.name_en || en })
  }
  numberedCache.set(districts, kids)
  return kids
}

// ── "the center" (engine v16) ─────────────────────────────────────────────────
// "ცენტრში ბინა" → one 🏙 token patching districtCodes with the CENTER_DISTRICT_CODES
// business set (OR semantics, identical to picking those districts by hand). Only
// codes this deployment's district list actually has are included. The pipeline
// DROPS this token when the query also names a non-Tbilisi city ("ბათუმის ცენტრი"
// must not inject Tbilisi districts — see compileQuery).
function recognizeCenter(text, districts) {
  const codes = CENTER_DISTRICT_CODES.filter(c => districts.some(d => d.code === c))
  if (!codes.length) return []
  const out = []
  const re = new RegExp(`(?:^|\\s)(${alt(CENTER_WORDS)})(?=\\s|$)`, 'giu')
  let m
  while ((m = re.exec(text))) {
    out.push({
      start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
      token: { sig: 'center', type: 'center', label: '🏙 ცენტრი', value: { codes } },
    })
  }
  return out
}

// ── the home city (engine v20, 2026-07-31) ────────────────────────────
// "თბილისი საბურთალო" → a chip patching districtCodes with every Tbilisi district, exactly
// as if the agent had ticked them in the panel. Same mechanism as recognizeCenter, and for
// the same reason: a city with no district row of its own can still be a filter if we name
// the set it stands for.
//
// WHY NOT a MUN_TBILISI row in the DB, which is the obvious fix: the hierarchy is raion
// (11 roots) -> ubani (52 children), and the RPC's dset expansion is ONE level
// (unnest(codes) union children-of-codes). A city row above the raions makes it three
// levels, so filtering Tbilisi would expand to the 11 raions and NOT the 52 ubani - every
// ubani-coded property (Vazisubani, Temka, Nutsubidze) would score mismatch and vanish.
// That is a worse bug than the one being fixed. Doing it in the compiler keeps the emitted
// filter FLAT, which is the shape the RPC already handles correctly.
//
// MEASURED CONSEQUENCE, deliberate: about half the leads corpus has no resolved district, so
// a Tbilisi filter moves those to the may-consider shelf rather than the matched list
// (19,954 -> 9,279 on the leads pool the day this shipped). That is the three-valued
// contract working - unknown is shelved, never silently dropped - and it is VISIBLE, unlike
// the 12% of properties the free-text path was discarding without a word. If it proves too
// aggressive in practice the honest alternative is a stopword, NOT a filter that quietly
// ignores unknowns.
const OLD_CITY_AFTER = /^\s+(?:القديمة|القديم)(?=\s|$)/u
const OLD_CITY_WORDS = /(?:^|\s)(?:old|dzveli|ძველი|старый|старого|старом|alt|eski)\s+$/iu
function recognizeHomeCity(text, districts) {
  const codes = districts
    .filter(d => TBILISI_CODE_PREFIXES.some(p => (d.code || '').startsWith(p)))
    .map(d => d.code)
  if (!codes.length) return []
  const out = []
  // v28: `\\s`, not `\s` — inside a template literal `\s` is a plain "s", so since v20 the word matched only as the
  // WHOLE input ("vake tbilisi", "tbilisi 2 bed" left it as unexplained text)
  const re = new RegExp(`(?:^|\\s)(${alt(TBILISI_WORDS)})(?=\\s|$)`, 'giu')
  let m
  while ((m = re.exec(text))) {
    // v28: "old tbilisi" / "ძველი თბილისი" / "старый тбилиси" is a PLACE (Old Tbilisi), not the city — the district
    // phrase pass must get it, so the city word yields when "old" stands right before it
    if (OLD_CITY_WORDS.test(text.slice(0, m.index + m[0].indexOf(m[1])))) continue
    if (OLD_CITY_AFTER.test(text.slice(m.index + m[0].length))) continue   // Arabic puts "old" after: تبليسي القديمة
    out.push({
      start: m.index + m[0].indexOf(m[1]), end: m.index + m[0].length,
      token: { sig: 'city:tbilisi', type: 'homecity', label: '📍 თბილისი', value: { codes } },
    })
  }
  return out
}

// ── near-location gazetteer (engine v7, 2026-07-16) ──────────────────────────
// "delisi metro" / "ვაკის პარკი" / "m2 kazbegi" → a near_location chip. The
// dictionary is the scheduler-DB `locations` table (name, name_ka, and the
// AI-generated ka/en/ru alias sets in meta.aliases — imported 2026-07-16); the
// caller passes the rows, this file stays data-free like districts.
//
// Two deliberate guards, both "unknown ≠ guess":
//  - a phrase used by MORE THAN ONE location is poisoned (never matched) — e.g.
//    "tavisufleba" is both Freedom Square (landmark) and Liberty Square (metro);
//  - a SINGLE-WORD phrase that is also a district name/alias is skipped, so a
//    bare "isani" stays the DISTRICT (the common intent) and never silently
//    becomes the metro station — "metro isani" (two words) matches the station.
const nearIndexCache = new WeakMap()   // locations array → built index
// EXPORTED (2026-08-16): the suggest dropdown's 📍 place row now commits a near filter
// directly from the location id, and its chip must read identically to the one this
// compiler mints for the same location from typed text. Two labels for one concept is
// how the district/place split became invisible in the first place — one map, one answer.
export const NEAR_TYPE_EMOJI = { metro: '🚇', park: '🌳', square: '⛲', mall: '🛍️', market: '🛍️', complex: '🏢', school: '🏫', kindergarten: '🧸', hospital: '🏥', landmark: '🏛️', airport: '✈️' }
// Station markers (engine v10): a word that PROMOTES the adjacent place name to a near-location
// even when that name is ALSO a district — "მეტრო დელისი" → the Delisi STATION, not the Delisi
// district. Stemmed, so "მეტროსთან" counts too.
const NEAR_MARKERS = new Set(METRO_MARKER_WORDS.map(w => georgianStem(w)))
function buildNearIndex(locations, districts) {
  // District words (names + aliases), STEMMED — the single-word deferral set.
  const districtStems = new Set()
  const addD = part => { const s = georgianStem(part.toLowerCase()); if (s) districtStems.add(s) }
  // Engine v13: only TBILISI districts (TBS_/PERI_) create deferral, and compound
  // official names are skipped (GENERIC_ADMIN_WORDS) — a word owned solely by a far-city
  // district (Batumi's "რუსთაველი" neighborhood, Rustavi settlements) made the gazetteer
  // defer to a district the Tbilisi-primary rule then SUPPRESSED, so bare "რუსთაველი"
  // vanished while cyrillic "руставели" resolved to the metro.
  for (const d of districts) {
    if (!(d.code.startsWith('TBS_') || d.code.startsWith('PERI_'))) continue
    for (const name of [d.name_ka, d.name_en, d.name_ru].filter(Boolean)) {
      const parts = name.toLowerCase().split(/[\s-]+/)
      if (parts.some(p => GENERIC_ADMIN_WORDS.has(p) || GENERIC_ADMIN_WORDS.has(georgianStem(p)))) continue
      for (const part of parts) addD(part)
    }
    // v29: the district's single-word names in the other scripts defer the same way ("アヴラバリ" is the district, as
    // "ავლაბარი" is — the station keeps its own "... metro" form)
    for (const name of Object.values(d.names || {})) if (name && !LATIN_ONLY.test(name) && !/\s/.test(name.trim())) addD(String(name).trim())
  }
  for (const aliases of Object.values(DISTRICT_ALIASES))
    for (const a of aliases) for (const part of a.split(/[\s-]+/)) addD(part)
  // Stem-based phrase index (engine v10): each phrase → its array of stemmed words, so an
  // inflected query ("დელისთან", "უნივერსიტეტთან") matches the base name via the SAME stemmer
  // the district recognizer uses — no enumerated inflection lists. A stem-sequence claimed by
  // MORE THAN ONE location is poisoned (never guessed), same rule as before.
  const byKey = new Map()   // stems.join(' ') → { stems, loc } | null (poisoned)
  const add = (loc, stems) => {
    if (!stems.length || stems.join('').length < 3) return
    const key = stems.join(' ')
    const cur = byKey.get(key)
    if (cur === undefined) byKey.set(key, { stems, loc })
    else if (cur && cur.loc.id !== loc.id) byKey.set(key, null)
  }
  // v30: a name is tokenised exactly as the query is — NFKC ("m²" = "m2"), Turkish İ, WORD_RE (CJK runs incl. ・, digits
  // and one-letter words kept). Dropping digits and letters made "Archi Dighomi 1/2/3" and "Archi Isani"/"Archi Isani A"
  // one key each, which the poison rule then removed for all of them.
  const nearStems = raw => (String(raw).normalize('NFKC').replace(/İ/g, 'I').toLowerCase().match(WORD_RE) || [])
    .map(w => georgianStem(w.replace(/\.+$/, ''))).filter(Boolean)
  const weak = []   // [loc, name without its bracketed qualifier] — added last, never poisoning a real name
  for (const loc of locations) {
    const phrases = [loc.name, loc.name_ka, ...(loc.meta?.aliases || loc.aliases || [])].filter(Boolean)
    for (const r of phrases) { const b = String(r).replace(/\s*[(（][^)）]*[)）]\s*/g, ' ').trim(); if (b && b !== String(r).trim()) weak.push([loc, b]) }
    for (const raw of phrases) {
      const stems = nearStems(raw)
      // Engine v11: a single-word phrase that IS a generic everyday word (ეზო "yard") never
      // matches — same collision guard as districts; multi-word names stay intact.
      if (stems.length === 1 && GENERIC_PLACE_STEMS.has(stems[0])) continue
      add(loc, stems)
    }
  }
  const weakBy = new Map()
  for (const [loc, name] of weak) { const st = nearStems(name), k = st.join(' '); if (byKey.has(k)) continue; const c = weakBy.get(k); weakBy.set(k, c === undefined ? { st, loc } : (c && c.loc.id === loc.id ? c : null)) }
  for (const v of weakBy.values()) if (v) add(v.loc, v.st)
  const entries = [...byKey.values()].filter(Boolean).map(v => ({
    stems: v.stems, loc: v.loc,
    deferrable: v.stems.length === 1 && districtStems.has(v.stems[0]),   // bare district-named word
  })).sort((a, b) => b.stems.length - a.stems.length)   // longest stem-sequence wins
  return { entries }
}
function recognizeNearLocation(text, locations, districts, opts = {}) {
  if (!locations?.length) return []
  let idx = nearIndexCache.get(locations)
  if (!idx) { idx = buildNearIndex(locations, districts); nearIndexCache.set(locations, idx) }
  if (!idx.entries.length) return []
  // Tokenize the query with positions; stem each word once. Sequence-match (not regex) so a
  // multi-word name matches an inflected multi-word query the same way single words do.
  const words = []
  const wre = new RegExp(WORD_RE.source, 'gu')   // v30: the same word rule as the index
  let m
  while ((m = wre.exec(text))) { const w = m[0].replace(/\.+$/, ''); if (w) words.push({ start: m.index, end: m.index + w.length, stem: georgianStem(w.toLowerCase()) }) }
  const isMarker = w => w && NEAR_MARKERS.has(w.stem)
  const consumed = new Array(words.length).fill(false)
  const out = []
  for (const e of idx.entries) {                       // longest-first: longer names win overlaps
    const L = e.stems.length
    // v38: the early pass (before the type words) takes only a WHOLE multi-word complex name that contains a type word
    if (opts.typePhrases && !(L >= 2 && e.loc.type === 'complex' && e.stems.some(st => TYPE_WORD_STEMS().has(st)))) continue
    for (let i = 0; i + L <= words.length; i++) {
      let ok = true
      for (let k = 0; k < L; k++) if (consumed[i + k] || words[i + k].stem !== e.stems[k]) { ok = false; break }
      if (!ok) continue
      const beforeMk = i > 0 && !consumed[i - 1] && isMarker(words[i - 1])
      const afterMk = i + L < words.length && !consumed[i + L] && isMarker(words[i + L])
      // Deferral (preserved): a bare district-named phrase becomes the STATION only when a metro
      // marker is adjacent AND the location is a metro; otherwise it defers to the district
      // recognizer, so bare "ისანი" stays the district exactly as before.
      if (e.deferrable && !(e.loc.type === 'metro' && (beforeMk || afterMk))) continue
      // Absorb ONE adjacent marker into the span for metro matches so "მეტრო" isn't left residual.
      let s = i, en = i + L - 1
      if (e.loc.type === 'metro') { if (beforeMk) s = i - 1; else if (afterMk) en = i + L }
      for (let k = s; k <= en; k++) consumed[k] = true
      const loc = e.loc
      out.push({
        start: words[s].start, end: words[en].end,
        token: {
          sig: `near:${loc.id}`, type: 'near_location',
          label: `${NEAR_TYPE_EMOJI[loc.type] || '📍'} ${loc.name}`,
          value: { id: loc.id, name: loc.name, name_ka: loc.name_ka || null, kind: loc.type, lat: loc.lat, lng: loc.lng, district: loc.district || null, ...(loc.handle ? { handle: loc.handle } : {}) },
        },
      })
    }
  }
  if (opts.typePhrases) return out   // the early pass is whole complex names only — never the first-word station rule below
  // v32: "მეტრო ახმეტელთან" — the station is "ახმეტელის თეატრი"; people say its first word. Only beside a metro word, and
  // only when exactly one station's name starts with that word.
  const firstOf = new Map()
  for (const e of idx.entries) if (e.loc.type === 'metro' && e.stems.length > 1) { const k = e.stems[0]; const c = firstOf.get(k); firstOf.set(k, c === undefined || (c && c.id === e.loc.id) ? e.loc : null) }
  for (let i = 0; i < words.length; i++) {
    if (consumed[i]) continue
    const loc = firstOf.get(words[i].stem)
    if (!loc) continue
    const mk = i > 0 && !consumed[i - 1] && isMarker(words[i - 1]) ? i - 1 : i + 1 < words.length && !consumed[i + 1] && isMarker(words[i + 1]) ? i + 1 : -1
    if (mk < 0) continue
    const s = Math.min(i, mk), en = Math.max(i, mk)
    consumed[s] = consumed[en] = true
    out.push({ start: words[s].start, end: words[en].end, token: { sig: `near:${loc.id}`, type: 'near_location', label: `${NEAR_TYPE_EMOJI[loc.type] || '📍'} ${loc.name}`,
      value: { id: loc.id, name: loc.name, name_ka: loc.name_ka || null, kind: loc.type, lat: loc.lat, lng: loc.lng, district: loc.district || null, ...(loc.handle ? { handle: loc.handle } : {}) } } })
  }
  out.sort((a, b) => a.start - b.start)
  return out
}

// ── restriction subjects (engine v18, 2026-07-28) ────────────────────────────
// "ინდოელები", "ძაღლი", "სტუდენტები" — the ~46 subjects the sidebar's Terms facet
// already filters by, now sayable in the box. The subject list is NOT a constant here:
// it is the SAME `features` rows (category 'tenant_terms') the chips render, so a
// subject added to the DB is searchable the day it appears, and the two surfaces can
// never drift into two vocabularies. Extra spellings/inflections live in
// RESTRICTION_ALIASES (searchVocab).
//
// Precedence: this runs AFTER the place recognizers on purpose. A subject word is
// never a place name, so nothing is lost by yielding — and if a future subject ever
// collides with a district, the place keeps winning without needing a cross-index.
//
// Matching: multi-word names ("დიდი ძაღლი") match as an exact adjacent phrase;
// single-word names additionally match through the Georgian stemmer, so ინდოელებზე /
// ძაღლით / სტუდენტს resolve. Longest phrase first, so "პატარა ძაღლი" claims its span
// before bare "ძაღლი" can.
const ALLOW_WORD_SET = new Set(RESTRICTION_ALLOW_WORDS.map(w => w.toLowerCase()))
const DENY_WORD_SET = new Set(RESTRICTION_DENY_WORDS.map(w => w.toLowerCase()))
const restrictionIndexCache = new WeakMap()
function restrictionIndex(features) {
  if (!Array.isArray(features) || !features.length) return []
  const hit = restrictionIndexCache.get(features)
  if (hit) return hit
  const skip = new Set(RESTRICTION_NON_SUBJECTS)
  const out = []
  for (const f of features) {
    // ⚠️ RETIRED CODES ARE NOT SUBJECTS (2026-08-05). The box and the sidebar share one
    // vocabulary by construction, so the facet's active-only rule has to hold here too —
    // otherwise typing "ინგლისურენოვანი" compiles a chip the panel refuses to draw, and the
    // agent gets a filter they cannot see or remove. `active !== false` keeps a server older
    // than the deploy that added the column behaving exactly as before.
    if (!f || f.category !== 'tenant_terms' || skip.has(f.code) || f.active === false) continue
    const display = f.name_ka || f.name_en || f.code
    const phrases = new Set()
    for (const n of [f.name_ka, f.name_en, f.name_ru]) {
      // Slash-form names ("რუსი / უკრ. / ბელ.", "გადარიცხვა / ოფიც. ხელშეკრულება") are
      // label shorthand, not something anyone types. They get aliases or nothing —
      // splitting them would mint ambiguous single words (რუსი belongs to pol_russians).
      if (!n || n.includes('/')) continue
      phrases.add(String(n).toLowerCase())
      // v26: "White frame (finished shell)" — nobody types the bracketed gloss, so the name WITHOUT it is
      // a phrase too. Every frame rung and a dozen features were untypeable in English because of it.
      { const bare = String(n).replace(/\s*\([^)]*\)\s*/g, ' ').trim().toLowerCase()
        if (bare && bare !== String(n).toLowerCase() && bare.length >= 3) phrases.add(bare) }
    }
    for (const a of RESTRICTION_ALIASES[f.code] || []) phrases.add(String(a).toLowerCase())
    for (const p of phrases) {
      const parts = p.split(/[\s.]+/).filter(Boolean)
      if (!parts.length || p.length < 3) continue
      const stem = parts.length === 1 ? georgianStem(parts[0]) : null
      // v24: every word of a multi-word phrase carries its stem too, so an inflected phrase ("ახალი
      // რემონტით", instrumental) still finds "ახალი რემონტი". A stem shorter than 3 never matches.
      const stems = parts.map(p => { const s = georgianStem(p); return s && s.length >= 3 ? s : null })
      out.push({ code: f.code, display, parts, stem: stem && stem.length >= 3 ? stem : null, stems })
    }
  }
  // Longest phrase first; within equal length, longer literal wins. Deterministic.
  out.sort((a, b) => b.parts.length - a.parts.length || b.parts.join(' ').length - a.parts.join(' ').length)
  restrictionIndexCache.set(features, out)
  return out
}
function recognizeRestriction(text, features) {
  const index = restrictionIndex(features)
  if (!index.length) return []
  const words = []
  const wre = /[\p{L}\p{N}]+/gu
  let wm
  while ((wm = wre.exec(text))) words.push({ w: wm[0], start: wm.index, end: wm.index + wm[0].length })
  if (!words.length) return []
  const stems = words.map(x => georgianStem(x.w))
  const gs = words.map(x => latinGe(x.w))                       // v31: Latin-typed Georgian ("avejith", "cxovelebit")
  const gStems = gs.map(g => (g ? georgianStem(g) : null))
  const out = []
  const claimed = []
  for (const entry of index) {
    const n = entry.parts.length
    for (let i = 0; i + n <= words.length; i++) {
      let ok = true
      for (let j = 0; j < n; j++) {
        const w = words[i + j].w
        if (w === entry.parts[j]) continue
        if (n === 1 && entry.stem && stems[i + j] === entry.stem) continue
        if (n > 1 && entry.stems && entry.stems[j] && stems[i + j] === entry.stems[j]) continue   // v24: per-word stem in a phrase
        if (latinMatches(gs[i + j], gStems[i + j], entry.parts[j], n === 1 ? entry.stem : entry.stems?.[j])) continue   // v31
        ok = false
        break
      }
      if (!ok) continue
      let start = words[i].start, end = words[i + n - 1].end
      // Which of the two intents? A bare subject is `hide` (the everyday act); an adjacent
      // allow-word ("მხოლოდ ინდოელები", "ინდოელები დაიშვება") switches to `allow` and is
      // consumed with it, so the modifier never leaks into the residual text search.
      // DENY is tested first: Georgian puts the negation between subject and verb
      // ("ინდოელები არ დაიშვება"), so the adjacent word is `არ` and the verb two out —
      // testing allow first would read the verb and invert the meaning.
      let mode = 'hide'
      const before = words[i - 1], after = words[i + n], after2 = words[i + n + 1]
      if (before && DENY_WORD_SET.has(before.w)) { mode = 'restricted'; start = before.start }
      else if (after && DENY_WORD_SET.has(after.w)) {
        mode = 'restricted'; end = after.end
        // consume the verb the negation governs, so it cannot survive as residual text
        if (after2 && ALLOW_WORD_SET.has(after2.w)) end = after2.end
      }
      // An allow-word does not make a THIRD state (owner, 2026-07-28: "we need two states
      // — it either is accepted and has a tick, or is not and has x"). "ინდოელები
      // დაიშვება" says the same thing as "ინდოელები": find places that take this client.
      // The word is still consumed, so it cannot survive into the residual text search.
      else if (before && ALLOW_WORD_SET.has(before.w)) start = before.start
      else if (after && ALLOW_WORD_SET.has(after.w)) end = after.end
      if (claimed.some(c => start < c.end && c.start < end)) continue
      claimed.push({ start, end })
      out.push({
        start, end,
        token: {
          sig: `restriction:${entry.code}:${mode}`, type: 'restriction',
          // The glyph IS the state (owner, 2026-07-28 — "shows emojis tick or x"):
          //   ✓ accepted     — find places that take this client (the default)
          //   ✗ not accepted — the ads that refuse them
          label: `${restrictionGlyph(mode)} ${entry.display}`,
          value: { code: entry.code, mode, display: entry.display },
        },
      })
    }
  }
  out.sort((a, b) => a.start - b.start)
  return out
}

// ── property features (engine v19, B37's amenity half) ────────────────────────
// "ლიფტი", "aivani", "პარკინგით" → the featureCodes filter, which the server sends as
// p_feature_codes. Presence-only: the RPC's three verdicts are pass / mismatch / unknown
// for a code the agent WANTS, and there is no "without a balcony" form — so this
// recognizer does not invent one. A negation someone types stays residual, which is the
// honest outcome and shows up in the query log as work to do.
//
// Vocabulary = SEARCH_FEATURE_CODES ∩ the live `feature` rows. The intersection is the
// point: the panel renders the same intersection, so the box can never compile a chip the
// sidebar cannot represent, and a code retired from the registry silently leaves both at
// once instead of becoming a corpus-wide whiteout (0241's measured failure mode).
//
// Precedence: after the place AND restriction recognizers. An amenity word is neither a
// place nor a tenant subject, so yielding costs nothing, and any future collision resolves
// toward the gazetteer — the same reasoning v18 recorded for restrictions.
const featureIndexCache = new WeakMap()
const conditionIndexCache = new WeakMap()
// v24 (F-100): ONE phrase-index builder for every vocabulary-backed family — amenity features and, now,
// the condition ladder ("ახალი რემონტი", "renovated", "თეთრი კარკასი"), which the panel filters through
// conditionCodes, a different field from featureCodes. Same names, same aliases, same stem rule.
const featureIndex = (features, extra = null) => {
  if (!extra || !extra.length) return phraseIndex(features, () => new Set(featureGroupsFrom(features, axisOf).flatMap(([, codes]) => codes)), featureIndexCache)
  // v32: the PUBLIC site filters some codes the Desk's Features facet does not show (project type: ჩეხური, ყავლაშვილის,
  // ხრუშჩოვკა …). The caller names them; the Desk passes none, so its box still only compiles what its panel can show.
  const key = [...extra].sort().join(',')
  let byKey = featureIndexByExtra.get(features)
  if (!byKey) { byKey = new Map(); featureIndexByExtra.set(features, byKey) }
  if (!byKey.has(key)) byKey.set(key, phraseIndex(features, () => new Set([...featureGroupsFrom(features, axisOf).flatMap(([, codes]) => codes), ...extra]), new WeakMap()))
  return byKey.get(key)
}
const featureIndexByExtra = new WeakMap()
const conditionIndex = (features, codes) => {
  // v26: the registry's rungs when the caller has them; the constant otherwise (byte-identical to v25)
  if (!codes || !codes.length) return phraseIndex(features, () => new Set(VOCAB.CONDITION_CODES), conditionIndexCache)
  const key = codes.join(',')
  let byCodes = conditionIndexByCodes.get(features)
  if (!byCodes) { byCodes = new Map(); conditionIndexByCodes.set(features, byCodes) }
  if (!byCodes.has(key)) byCodes.set(key, phraseIndex(features, () => new Set(codes), new WeakMap()))
  return byCodes.get(key)
}
const conditionIndexByCodes = new WeakMap()
function phraseIndex(features, wantedOf, cache) {
  if (!Array.isArray(features) || !features.length) return []
  const hit = cache.get(features)
  if (hit) return hit
  // 2026-08-27: the box and the panel must understand the SAME codes, so both derive from
  // the registry through featureGroupsFrom(). Before this the set was the 26-code hand-typed
  // array, and "აუზი" compiled to nothing against a corpus holding 2,919 pools.
  const wanted = wantedOf()
  const out = []
  for (const f of features) {
    if (!f || !wanted.has(f.code)) continue
    const display = f.name_ka || f.name_en || f.code
    const phrases = new Set()
    for (const n of [f.name_ka, f.name_en, f.name_ru]) {
      // Slash-form names are label shorthand nobody types ("ქურა (გაზის/ელექტრო)") — same
      // rule as the restriction index. They get an explicit alias or nothing.
      if (!n || n.includes('/')) continue
      phrases.add(String(n).toLowerCase())
      // v26: "White frame (finished shell)" — nobody types the bracketed gloss, so the name WITHOUT it is
      // a phrase too. Every frame rung and a dozen features were untypeable in English because of it.
      { const bare = String(n).replace(/\s*\([^)]*\)\s*/g, ' ').trim().toLowerCase()
        if (bare && bare !== String(n).toLowerCase() && bare.length >= 3) phrases.add(bare) }
    }
    for (const a of f.aliases || []) {
      const s = String(a).toLowerCase()
      // ⚠️ The DB `aliases` column carries INGEST keys next to human words — 'lb_parking_yes',
      // 'underground_parking', 'coded-door' are portal label ids the scraper resolves by, not
      // search words (feature-names-are-matching-keys). snake_case entries are dropped rather
      // than left inert: they can never match the word tokenizer anyway, and keeping them in
      // the index only makes the longest-phrase sort lie about what it is ordering.
      if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(s)) continue
      phrases.add(s)
    }
    for (const a of FEATURE_ALIASES[f.code] || []) phrases.add(String(a).toLowerCase())
    for (const p of phrases) {
      // Hyphen splits here (it does not in the restriction index): the alias forms that
      // reach this list are written the way people type them ("wi-fi"), so the hyphen is
      // a word separator, not part of a token.
      const parts = p.split(/[\s.\-]+/).filter(Boolean)
      if (!parts.length || p.length < 3) continue
      const stem = parts.length === 1 ? georgianStem(parts[0]) : null
      // v24: every word of a multi-word phrase carries its stem too, so an inflected phrase ("ახალი
      // რემონტით", instrumental) still finds "ახალი რემონტი". A stem shorter than 3 never matches.
      const stems = parts.map(p => { const s = georgianStem(p); return s && s.length >= 3 ? s : null })
      out.push({ code: f.code, display, parts, stem: stem && stem.length >= 3 ? stem : null, stems })
    }
  }
  out.sort((a, b) => b.parts.length - a.parts.length || b.parts.join(' ').length - a.parts.join(' ').length)
  cache.set(features, out)
  return out
}
// v33: the words around a feature can say it is ABSENT. "ლიფტის გარეშე" / "without a lift" / "no balcony" / "ლიფტი არ აქვს"
// used to compile to the feature PRESENT (and leave "გარეშე" as unread text) — the opposite of the request, for every
// amenity. The negation word is consumed with the feature. Public mode emits the ABSENT token (the public search has a
// yes/no family per feature, so the filter is "no"). The Desk's RPC has no "without X" verdict (the v19 note above), so in
// Desk mode the words are left alone as text: never the opposite filter, and never a chip that filters nothing.
const NEG_BEFORE = new Set(FEATURE_NEGATION_BEFORE.map(w => w.toLowerCase()))
const NEG_AFTER = new Set(FEATURE_NEGATION_AFTER.map(w => w.toLowerCase()))
const NEG_VERBS = new Set(FEATURE_NEGATION_VERBS.map(w => w.toLowerCase()))
const NEG_ARTICLES = new Set(FEATURE_NEGATION_ARTICLES.map(w => w.toLowerCase()))
function recognizeFeature(text, features, extra, publicMode) {
  const hits = recognizePhrases(text, featureIndex(features, extra), 'feature', d => `✚ ${d}`)
  if (!hits.length) return hits
  const words = []
  const wre = /[\p{L}\p{N}]+/gu
  let wm
  while ((wm = wre.exec(text))) words.push({ w: wm[0].toLowerCase(), start: wm.index, end: wm.index + wm[0].length })
  const out = []
  for (const h of hits) {
    const first = words.findIndex(x => x.start === h.start)
    const last = words.findIndex(x => x.end === h.end)
    if (first < 0 || last < 0) { out.push(h); continue }
    let start = h.start, end = h.end, negated = false
    // BEFORE: "without a lift", "no balcony", "არა ლიფტი"
    let b = first - 1
    if (b >= 0 && NEG_ARTICLES.has(words[b].w)) b--
    if (b >= 0 && NEG_BEFORE.has(words[b].w)) { negated = true; start = words[b].start }
    // AFTER: "ლიფტის გარეშე" (Georgian puts "without" after the noun) and "ლიფტი არ აქვს"
    const a1 = words[last + 1], a2 = words[last + 2]
    if (!negated && a1 && NEG_AFTER.has(a1.w)) { negated = true; end = a1.end }
    else if (!negated && a1 && a1.w === 'არ' && a2 && NEG_VERBS.has(a2.w)) { negated = true; end = a2.end }
    if (!negated) { out.push(h); continue }
    if (!publicMode) continue   // Desk: leave the words as text rather than invent a filter the RPC cannot answer
    const { code, display } = h.token.value
    out.push({ start, end, token: { sig: `feature:${code}:absent`, type: 'feature', label: `⊘ ${display}`, value: { code, display, negated: true } } })
  }
  return out
}
const recognizeCondition = (text, features, codes) => recognizePhrases(text, conditionIndex(features, codes), 'condition', d => `🏗 ${d}`)
function recognizePhrases(text, index, type, labelOf) {
  if (!index.length) return []
  const words = []
  const wre = /[\p{L}\p{N}]+/gu
  let wm
  while ((wm = wre.exec(text))) words.push({ w: wm[0], start: wm.index, end: wm.index + wm[0].length })
  if (!words.length) return []
  const stems = words.map(x => georgianStem(x.w))
  const gs = words.map(x => latinGe(x.w))                       // v31: Latin-typed Georgian ("avejith", "cxovelebit")
  const gStems = gs.map(g => (g ? georgianStem(g) : null))
  const out = []
  const claimed = []
  for (const entry of index) {
    const n = entry.parts.length
    for (let i = 0; i + n <= words.length; i++) {
      let ok = true
      for (let j = 0; j < n; j++) {
        const w = words[i + j].w
        if (w === entry.parts[j]) continue
        if (n === 1 && entry.stem && stems[i + j] === entry.stem) continue
        if (n > 1 && entry.stems && entry.stems[j] && stems[i + j] === entry.stems[j]) continue   // v24: per-word stem in a phrase
        if (latinMatches(gs[i + j], gStems[i + j], entry.parts[j], n === 1 ? entry.stem : entry.stems?.[j])) continue   // v31
        ok = false
        break
      }
      if (!ok) continue
      const start = words[i].start, end = words[i + n - 1].end
      if (claimed.some(c => start < c.end && c.start < end)) continue
      claimed.push({ start, end })
      out.push({
        start, end,
        token: {
          sig: `${type}:${entry.code}`, type,
          // ✚ = "with" — deliberately not ✓, which belongs to the restriction family and
          // means an owner's recorded stance. These two chip kinds sit in the same row.
          label: labelOf(entry.display),
          value: { code: entry.code, display: entry.display },
        },
      })
    }
  }
  out.sort((a, b) => a.start - b.start)
  return out
}

// Subject completion hints (engine v18) — same dictionary the recognizer uses, so a hint
// can never offer something the compiler would then fail to understand (the suggestDistricts
// rule). Returns the subject PLUS the intents available for it, because a subject alone does
// not say which one you mean (owner, 2026-07-28) — the dropdown renders one row per intent
// so the choice is made while typing instead of discovered afterwards in the sidebar.
export function suggestRestrictions(text, { features = [], limit = 4 } = {}) {
  const raw = String(text || '')
  if (!raw) return []
  const m = raw.toLowerCase().match(/[\p{L}\p{N}]+$/u)
  if (!m || m[0].length < 3) return []
  const word = m[0]
  const stem = georgianStem(word)
  const byCode = new Map()
  for (const entry of restrictionIndex(features)) {
    if (entry.parts.length !== 1) continue
    const p = entry.parts[0]
    if (!(p.startsWith(word) || (entry.stem && entry.stem.startsWith(stem)))) continue
    if (!byCode.has(entry.code)) byCode.set(entry.code, { code: entry.code, display: entry.display, insert: p })
    else if (p.length < byCode.get(entry.code).insert.length) byCode.get(entry.code).insert = p
  }
  return [...byCode.values()].sort((a, b) => a.insert.length - b.insert.length).slice(0, limit)
}

// The text a clicked hint puts in the box. Lives HERE, next to the recognizer that has to
// read it back, so the round-trip is one file's problem: click "only if allowed" → this
// phrase → recognizeRestriction → the same mode. Pinned by a test.
// THE glyph for a restriction mode. One definition, because there were two and they
// DISAGREED (2026-07-29, owner-reported: picking the "accepted" chip rendered ✗).
// The compiler used `mode === 'restricted' ? '✗' : '✓'` while the committed-chip renderer
// in IntakeTab used `mode === 'hide' ? not_allowed : allowed` — written before the
// 2026-07-28 ruling, when `hide` still meant "exclude these". The ruling redefined `hide`
// as "find the ones who ACCEPT", so that second mapping silently inverted.
//   hide       → ✓ accepted      (find places that take this client — the default)
//   restricted → ✗ not accepted  (the ads that refuse them)
// Anything rendering a restriction mode calls this. Do not re-derive the glyph.
export const restrictionGlyph = (mode) => (mode === 'restricted' ? '✗' : '✓')

export function restrictionPhrase(display, mode) {
  // ✗ "X არ დაიშვება"; ✓ is the bare subject — the default needs no modifier, and adding
  // one ("X დაიშვება") would put a word in the box that changes nothing.
  if (mode === 'restricted') return `${display} არ ${RESTRICTION_ALLOW_WORDS[0]}`
  return String(display)
}

// ── policy: semantic value → filter fields ────────────────────────────────────
// RECOGNITION is linguistic ("სამ ოთახიანი" MEANS rooms=3); APPLICATION is policy
// (in the browse panel, rooms=3 filters as exactly 3; a spoken area is ±10%). Kept
// apart so a different surface (matching: rooms=3 → ideal 3, acceptable 2–4) can
// apply the SAME recognized token differently without touching a recognizer — and
// so policy calls like "exact vs at-least" are named decisions here, not buried
// in regex handlers. Logged tokens carry BOTH value and patch: what was understood
// and what this surface did with it.
const BROWSE_POLICY = {
  // Counts are exact — checkbox-set fields since v9 (2026-07-17 facet sidebar): the set
  // member is the checkbox that lights up. 5 is the "5+" box (≥5 semantics), so a spoken
  // count above 5 folds into it — browse-time policy, the token still logs the raw n.
  rooms:    v => ({ roomsSet: [...new Set((v.set || [v.n]).map(n => Math.min(n, 5)))] }),      // v24: a range carries its set
  bedrooms: v => ({ bedroomsSet: [...new Set((v.set || [v.n]).map(n => Math.min(n, 5)))] }),
  // engine v25: "2 bath" is exactly two, the same range the panel's bathroom pill writes (min=max=n)
  bathrooms: v => ({ bathroomsMin: String(v.n), bathroomsMax: String(v.n) }),
  // engine v25: studio is the recorded layout fact, the SAME featureCodes filter the Features facet writes
  studio:   () => ({ featureCodes: ['layout_studio'] }),
  // v26: a range carries min/max; v36: an open bound ("3 სართულზე მაღლა") writes only its own side
  floor:    v => ({ ...(v.open !== 'max' ? { floorMin: String(v.min ?? v.n) } : {}), ...(v.open !== 'min' ? { floorMax: String(v.max ?? v.n) } : {}) }),
  // v26: the panel's own ✕ halves of the first/top floor flags (spec exclude.state firstFloorNot/topFloorNot)
  floor_flag: v => (v.flag === 'not_first' ? { firstFloorNot: true } : { topFloorNot: true }),
  // v26: an explicit area range/bound is exactly that — no ±10 % window
  area_range: v => ({ ...(v.min != null ? { areaMin: String(v.min) } : {}), ...(v.max != null ? { areaMax: String(v.max) } : {}) }),
  // v26: public mode only (the Desk never emits it); no Desk panel field, so no Desk patch
  metro_near: () => ({}),
  // v40: public only — the site's min_lease family (publicQuery.js); the Desk has no lease filter and never emits this
  lease: () => ({}),
  // "N/M" total (engine v15) — the building's storey count is a real panel range field.
  floors_total: v => ({ floorsTotalMin: String(v.n), floorsTotalMax: String(v.n) }),
  // "ბოლო სართული" (engine v15) — boolean cross-column predicate (floor = floors_total),
  // served by RPC v11 p_top_floor; client-side classify mirrors it for loaded rows.
  top_floor: () => ({ topFloor: true }),
  area:     v => ({ areaMin: String(Math.round(v.n * 0.9)), areaMax: String(Math.round(v.n * 1.1)) }), // spoken area ≈ ±10%
  // v24: a currency the agent did not say is not written — the panel's own setting stays in charge
  price:    v => ({ ...(v.bound === 'min' ? { priceMin: String(v.amount) } : { priceMax: String(v.amount) }),
                    ...(v.currencySpoken === false ? {} : { currency: v.currency }) }),
  // v37: price per m² (core 3872), in the same currency rule as the total price
  ppm:      v => ({ ...(v.bound === 'min' ? { ppmMin: String(v.amount) } : { ppmMax: String(v.amount) }),
                    ...(v.currencySpoken === false ? {} : { currency: v.currency }) }),
  deal:     v => ({ intents: [v.value] }),
  kind:     v => ({ kinds: [v.value] }),
  // context (engine v35) — an apartment run as part of a hotel operation (core 1940). The SAME
  // two fields the tree's apart-hotel row writes: kind apartment + its feature code.
  context:  v => ({ kinds: ['apartment'], featureCodes: [APARTMENT_CONTEXT[v.value]] }),
  // subtype (engine v21) — the grammar's Identity layer 3. Emits BOTH fields: the subtype
  // is what the agent said, the kind is what it implies, and sending the kind too means a
  // typed subtype can never return LESS reach than the bare kind word did before v16.
  // ⚠️ The RPC's subtype verdict is three-valued: a property with no subtype claim scores
  // `unknown` and lands on the possible-matches shelf, never in mismatch. That is the
  // honest answer where coverage is thin (office 10 of 644 commercial) and the right one
  // where it is not (agricultural 791 of 891 land) — but it DOES mean the headline count
  // for a thin subtype is small with a large shelf beside it. Coverage is a DATA question
  // (discovery is still widening), not a reason to compile the word to its parent kind.
  subtype:  v => ({ subtypes: [v.value], kinds: [SUBTYPE_PARENT_KIND[v.value]] }),
  // v26: a name family ticks every member; v36: a negated district ("არა ვაკე", "except vake") is the panel's ✕ half
  district: v => (v.negated ? { districtCodesNot: v.codes ? [...v.codes] : [v.code] } : { districtCodes: v.codes ? [...v.codes] : [v.code] }),
  // "ცენტრი" (engine v16) — the business definition of central Tbilisi as a district set.
  center: v => ({ districtCodes: [...v.codes] }),
  // v20: identical patch shape to `center` — both name a SET of real districts, so both
  // land in the same array field the panel's checkboxes write. A token type with no entry
  // here is recognised, chipped, and then filters NOTHING (silently), which is the
  // half-existing-key trap in another costume.
  homecity: v => ({ districtCodes: [...v.codes] }),
  // listing_id filters by identity, not by a panel field — the match itself lives in
  // IntakeTab's visibleResult (source_listing_id / source_url), so there is nothing to
  // patch; the empty patch still yields a chip + dismissal via the token's sig.
  listing_id: () => ({}),
  // F-103: the pasted enquiry template filters NOTHING by itself — the link beside it is the search.
  enquiry: () => ({}),
  // near_location (engine v7) filters by DISTANCE (listing coords vs the location's
  // point), also in visibleResult — no panel field exists for it, same empty-patch
  // chip-and-dismiss contract as listing_id.
  near_location: () => ({}),
  // phone (engine v8) — identity filter like listing_id: resolved DB-wide via
  // /listings/search's phone-suffix match; no panel field.
  phone: () => ({}),
  // cadastral (engine v23) — identity filter like listing_id/phone: resolved DB-wide via
  // /listings/search's prefix match, no panel field to set.
  cadastral: () => ({}),
  // restriction (engine v18) — the Terms facet's own field. `hide` is the everyday
  // reading of a typed subject ("my client has a dog"); the panel chip and this token
  // write the SAME "<code>:<mode>" string, so a spoken subject and a clicked chip are
  // literally the same filter and cannot disagree.
  restriction: v => ({ restrictions: [`${v.code}:${v.mode}`] }),
  // v24 (F-100): the condition ladder's own field — the SAME array the panel's rung click writes.
  condition: v => ({ conditionCodes: [v.code] }),
  // v24 (F-100): where the advert is listed — the source facet's own array.
  source: v => ({ sources: [v.value] }),
  // feature (engine v19) — the Features facet's own field, same one-filter-two-writers
  // contract as restrictions above: the checkbox and the spoken word patch the identical
  // array, so they can never mean two different things.
  // v33: an ABSENT feature (public mode only — the Desk never emits one) carries no Desk filter: the RPC has no "without"
  feature: v => (v.negated ? {} : { featureCodes: [v.code] }),
}

// ── district completion hints (engine v2, 2026-07-06) ────────────────────────
// Suggestions for the LAST partial word of the query — same dictionary the
// recognizer uses (DB names + DISTRICT_ALIASES), so a hint can never suggest
// something the compiler wouldn't then recognize. Deterministic prefix match,
// shortest completion per district first; the UI renders them as tappable chips
// and Tab accepts the first. Space deliberately does NOT accept (agents type
// multi-word queries — space after every word would grab whatever was showing).
export function suggestDistricts(text, { districts = [], limit = 5 } = {}) {
  const raw = String(text || '')
  if (!raw || /\s$/u.test(raw)) return []
  const m = raw.match(/[\p{L}\p{N}.]+$/u)
  if (!m || m[0].length < 2) return []
  const word = m[0].toLowerCase()
  const stem = georgianStem(word)
  const seen = new Map()   // code -> best (shortest) completion
  const consider = (part, code, display) => {
    const p = part.toLowerCase()
    if (p.length < 3 || p === word) return
    // Engine v11: never suggest a generic word (ძველი, დიდი, მასივი…) — accepting the hint
    // would insert a word the single-word recognizer now deliberately refuses to match.
    if (GENERIC_PLACE_STEMS.has(georgianStem(p))) return
    if (!(p.startsWith(word) || georgianStem(p).startsWith(stem))) return
    const cur = seen.get(code)
    if (!cur || p.length < cur.insert.length) seen.set(code, { code, display, insert: p })
  }
  for (const d of districts) {
    for (const name of [d.name_ka, d.name_en].filter(Boolean)) {
      for (const part of name.split(/[\s-]+/)) consider(part, d.code, d.name_en || d.name_ka)
    }
  }
  for (const [code, aliases] of Object.entries(DISTRICT_ALIASES)) {
    const d = districts.find(x => x.code === code)
    if (!d) continue
    for (const a of aliases) {
      for (const part of a.split(/[\s-]+/)) consider(part, code, d.name_en || d.name_ka)
    }
  }
  return [...seen.values()].sort((a, b) => a.insert.length - b.insert.length).slice(0, limit)
}

// ── the pipeline ──────────────────────────────────────────────────────────────
// Order = precedence: a span consumed by an earlier recognizer is invisible to
// later ones ("3 rooms" must not also become "price 3"; "vake" must not be
// re-read after the district hit).
// ── INVISIBLE CHARACTERS (engine v22, B645 — owner-reported 2026-08-12) ───────────────────
// A phone pasted from WhatsApp/iOS is not the string it looks like. The reported one was
// `557 75 13 77‬`: NO-BREAK SPACEs between the groups, and a trailing
// U+202C POP DIRECTIONAL FORMATTING. Every phone pattern ends in `(?=\s|$)`, and U+202C is
// NOT in JS `\s`, so the lookahead failed. Removing the spaces left `557751377‬` — still
// failing — and one BACKSPACE deleted the invisible character and "made it a number". That
// unexplainable-looking third step is the signature of this bug.
//
// ⚠️ REPLACED IN PLACE, NEVER DELETED. The compiler records matched SPANS for the highlight
// mirror and for `search_query_log`; dropping characters would shift every offset after the
// first one. Each of these maps to exactly one space, so `raw.length` is unchanged and every
// existing offset stays correct.
//
// Not a phone fix: this runs before ALL recognizers, so a pasted district name or listing id
// carrying the same marks stops failing too.
const INVISIBLE_RE = /[   ​-‏‪-‮⁦-⁩﻿]/g
export function normalizeInvisible(s) { return String(s || '').replace(INVISIBLE_RE, ' ') }

// v26 options (all optional; omitted = the Desk exactly as before):
//   conditionCodes  — the REGISTRY condition ladder's codes (0788-0790); the panel reads the registry, so
//                     the box must too, or "მშენებარე"/"under construction" stay untypeable. Falls back
//                     to the constant when absent.
//   mode: 'public'  — the public site: bare digits are never portal listing ids, and "near metro" (no
//                     station) is a distance band.
//   bareNumberPrice — { deal } — a lone deal-plausible number becomes a max price (public owner ruling);
//                     the deal is the text's own deal word when it has one, else this one.
export function compileQuery(text, { districts = [], locations = [], features = [], conditionCodes = null, mode = 'desk', bareNumberPrice = null, homeCity = null, locale = null, extraFeatureCodes = null, isSalePrice = null } = {}) {
  const publicMode = mode === 'public'
  const raw = normalizeInvisible(text)
  if (!raw.trim()) return { tokens: [], residual: '', failures: [], dropped: [], sentence: null, vocabVersion: VOCAB_VERSION, vocabHash: VOCAB_HASH, engineVersion: ENGINE_VERSION, ms: 0 }
  // v29: Turkish İ lowercases to i + a combining dot (two code units), which matched nothing and shifted every span;
  // İ → I first keeps the length, so spans still point at the typed characters
  const lower = raw.replace(/İ/g, 'I').toLowerCase()

  const RECOGNIZERS = [
    t => recognizeListingRef(t, { publicMode }),     // engine v5: FIRST — a URL span must be claimed whole before
                             // rooms/price recognizers can pick digits out of its slug
    recognizeEnquiryPaste,   // F-103: the portal's pasted enquiry template — after the URL is claimed, before any word in it can become a filter
    recognizeCadastral,      // engine v23: a dotted parcel code — BEFORE the numeric recognizers,
                             // because "01.14.05.008" is also the shape the grouped-thousands
                             // price rule reaches for, and spans are first-claim-wins
    recognizePhone,          // engine v8: 9-15 digit runs / +prefixed / GE-mobile-with-separators
                             // (bare listing ids stop at 8 digits, so no overlap with the above)
    t => (publicMode ? recognizeLeaseMonths(t) : []),   // engine v40: "for 6 months" — public only, before every number shape
    recognizeFloorFlag,      // engine v26: "not first floor" — before ANY floor or count shape can claim "first floor"
    recognizeFloorBound,     // engine v36: "3 სართულზე მაღლა" / "მე-3 სართულიდან" — before the single number+unit shape
    recognizeFloorRange,     // engine v26: "1-3 floor" — before the single number+unit shape
    recognizeAreaRange,      // engine v26: "70-90 sqm" / "at least 100 m²" — before the single number+unit shape
    t => recognizeCountMin(t, ROOM_WORDS, 'rooms'),         // engine v36: "მინიმუმ 3 ოთახი" / "3+ ოთახი"
    t => recognizeCountMin(t, BEDROOM_WORDS, 'bedrooms'),
    t => recognizeNumUnit(t, ROOM_WORDS, 'rooms', v => `${v} rooms`),
    recognizeRoomValueWord,  // engine v4: words that are a count (двушка/orotaxiani)
    t => recognizeNumUnit(t, BEDROOM_WORDS, 'bedrooms', v => `${v} bedrooms`),
    recognizeBedroomsPlus,   // engine v4: N+1 notation → bedrooms=N (ambiguous as rooms)
    recognizeRoomPrefix,     // engine v25: "T3" → rooms=3 (a total-room convention)
    t => recognizeNumUnit(t, BATHROOM_WORDS, 'bathrooms', v => `${v} bathrooms`),   // engine v25
    recognizeStudio,         // engine v25: "studio" → the layout_studio fact
    recognizeTopFloor,       // engine v15: "ბოლო სართული" phrase — before the unit-word shape
                             // (it contains 'სართული' but carries no number to misread)
    recognizeFloorOfTotal,   // engine v13: "5/9" = floor 5 of 9 — before the unit-word shape
    t => recognizeNumUnit(t, FLOOR_WORDS, 'floor', v => `floor ${v}`),
    t => recognizeUnitFirstFloor(t),   // v24: "floor 5" (English word order)
    t => recognizeNumUnit(t, AREA_UNITS, 'area', v => `≈${v} m²`),
    recognizeGlued,          // v32: "ბინისყიდვა" — two words typed as one
    t => recognizeWordMap(t, DEAL_WORDS, 'deal'),
    recognizeSource,   // v24: "myhome", "korter" — where the advert is listed
    // engine v21: BEFORE kind. Spans are first-claim-wins, and every subtype word is a
    // MORE specific statement than the kind word it used to compile to — "კოტეჯი" must
    // claim its own span as cottage, not be swallowed as house. The policy emits the
    // parent kind alongside, so ordering changes precision, never reach.
    // v29: a district's WHOLE multi-word name before stations, landmarks and the city word — "Vazha-Pshavela Avenue 1st
    // Block" is that quarter, not the Vazha-Pshavela metro; "Tbilisi Sea Area" is not the city. Only exact whole names.
    // v31: whole multi-word district names BEFORE the type words — "Mukhiani Cottages" is a place, and plural type words
    // (cottages, აგარაკები) are vocabulary now
    t => recognizeDistrict(t, districts, { publicMode, homeCity: publicMode ? homeCity : null, locale, phrasesOnly: true }),
    // engine v35: apart-hotel BEFORE subtype — "apart hotel" must not fall to the commercial `hotel`
    // word. Desk only: the public site has no apart-hotel filter yet, so it keeps today's reading.
    t => (publicMode ? [] : recognizeWordMap(t, CONTEXT_WORDS, 'context')),
    // v38 (2026-10-28): a complex whose whole name carries a type word ("Jikia House", "Dream House Varketili",
    // "منزل في ساحة أوروبا", "ახალი სახლი ცაგარელზე") is that complex — the type word used to take "House" and leave
    // the rest as text. Same rule as the whole district names above; a lone type word is untouched.
    t => recognizeNearLocation(t, locations, districts, { typePhrases: true }),
    t => recognizeWordMap(t, SUBTYPE_WORDS, 'subtype'),
    t => recognizeWordMap(t, KIND_WORDS, 'kind'),
    // engine v7: BEFORE districts — "vake park" must be the park, not district vake +
    // residual "park"; the single-word guard in buildNearIndex keeps bare "isani" district.
    t => recognizeNearLocation(t, locations, districts),
    t => (publicMode ? recognizeMetroNear(t) : []),   // engine v26: AFTER the gazetteer — "delisi metro" is the station
    t => recognizeCenter(t, districts),   // engine v16: before districts (no district is named ცენტრი,
                                          // but keep the span claimed before the prefix matcher runs)
    // engine v20: BEFORE districts - "ძველი თბილისი" (TBS_OLD) is a real district name
    // containing this word, so the span must be claimed before the prefix matcher can
    // resolve a bare "თბილისი" to Old Tbilisi.
    t => recognizeHomeCity(t, districts),
    t => recognizeDistrict(t, districts, { publicMode, homeCity: publicMode ? homeCity : null, locale }),
    // engine v18: AFTER the place recognizers — a subject word is never a place, so
    // yielding costs nothing and any future collision resolves toward the gazetteer.
    t => recognizeRestriction(t, features),
    // engine v19: after restrictions — the two vocabularies are disjoint by construction
    // (tenant_terms is excluded from SEARCH_FEATURE_CODES), so the order is about future
    // additions, not today's words.
    t => recognizeFeature(t, features, extraFeatureCodes, publicMode),
    t => recognizeCondition(t, features, conditionCodes),   // v24: the condition ladder's own words (v26: the registry's rungs when given)
    recognizePrice,          // last: bare numbers only get to be prices if nothing else claimed them
    // engine v26: after EVERYTHING, and only when asked — the deal is read from the tokens so far
    t => { if (!bareNumberPrice) return []; const said = tokens.find(k => k.type === 'deal')?.value?.value; return recognizeBareNumberPrice(t, said || bareNumberPrice.deal, !!said) },
  ]

  const t0 = performance.now?.() ?? Date.now()
  const consumed = []        // [{start,end}] — spans already claimed
  const overlaps = (a, b) => a.start < b.end && b.start < a.end
  const tokens = []
  const seen = new Set()     // one chip per sig even if typed twice
  // v36 (2026-10-22): spans that were UNDERSTOOD but carry no token of their own — a repeat of a meaning, or a token a
  // rule below drops ("თბილისი" beside ვაკე, "ცენტრი" beside ბათუმი). They are not residual text, but they stayed in
  // the box after a commit and were re-read later without their context ("ცენტრი" alone = central Tbilisi). Returned
  // as `dropped` [{start,end,sig}] so the commit can take them out together with the folded tokens.
  const dropped = []
  for (const rec of RECOGNIZERS) {
    for (const hit of rec(lower)) {
      if (consumed.some(c => overlaps(c, hit))) continue
      // v31: the same meaning said twice ("kerdzo sakhli" — both are house) is understood, not left over as unread text
      if (seen.has(hit.token.sig)) { consumed.push({ start: hit.start, end: hit.end }); dropped.push({ start: hit.start, end: hit.end, sig: hit.token.sig }); continue }
      consumed.push({ start: hit.start, end: hit.end })
      seen.add(hit.token.sig)
      // `matched` = the exact input text this recognition consumed (original casing).
      // Logged with every token: makes each compiler decision a replayable OBSERVATION
      // ("input ვაკეში → district TBS_VAKE under vocab v7"), not just a final result —
      // future vocab versions can be evaluated against old logs without old code.
      // patch is derived HERE via policy, never inside a recognizer (see BROWSE_POLICY).
      tokens.push({
        ...hit.token,
        patch: BROWSE_POLICY[hit.token.type](hit.token.value),
        matched: raw.slice(hit.start, hit.end),
        // start/end (2026-07-24): character span in the RAW text — the inline-keyword
        // renderer highlights exactly these ranges in the field. Additive metadata,
        // no effect on recognition (no vocab bump).
        start: hit.start, end: hit.end,
      })
    }
  }

  // Engine v16: "ბათუმის ცენტრი" — a center token alongside a non-Tbilisi CITY district
  // would inject Tbilisi districts into a far-city search; the city wins, center drops.
  // (The span stays consumed: the word was understood, just not applied — the token
  // simply doesn't reach the panel or the residual text search.)
  // v36 (2026-10-22): a NEGATED district — "არა ვაკე", "not vake", "except vake", "кроме ваке", "გარდა ვაკისა", "ვაკის
  // გარდა" — all searched IN Vake. The negation word joins the token's span and the patch is the district ✕
  // (districtCodesNot), the same array the panel's ✕ writes. Runs before the rules below, which read only includes.
  {
    const negBefore = new RegExp(`(?:^|\\s)(${alt(DISTRICT_NEGATION_BEFORE.map(w => w.toLowerCase()))})\\s+$`, 'iu')
    const negAfter = new RegExp(`^\\s+(${alt(DISTRICT_NEGATION_AFTER.map(w => w.toLowerCase()))})(?=\\s|$|[.,;!?])`, 'iu')
    for (const t of tokens) {
      if (t.type !== 'district') continue
      const b = negBefore.exec(lower.slice(0, t.start))
      const a = b ? null : negAfter.exec(lower.slice(t.end))
      if (!b && !a) continue
      const start = b ? b.index + b[0].indexOf(b[1]) : t.start
      const end = a ? t.end + a[0].length : t.end
      if (consumed.some(c => c.start < end && start < c.end && !(c.start === t.start && c.end === t.end))) continue
      const value = { ...t.value, negated: true }
      Object.assign(t, { sig: `not:${t.sig}`, label: `✕ ${t.label}`, value, patch: BROWSE_POLICY.district(value), start, end, matched: raw.slice(start, end) })
      consumed.push({ start, end })
    }
  }
  // …and a set-valued place beside it ("არა ვაკე თბილისი", "ცენტრი გარდა სოლოლაკისა") loses the excluded subtree: an include
  // inside a ✕ subtree is a carve-out (core 0886) and would give the subtree back.
  const notCodes = new Set(tokens.filter(t => t.type === 'district' && t.value.negated).flatMap(t => t.value.codes || [t.value.code]))
  if (notCodes.size) {
    const parentOf = new Map(districts.map(d => [d.code, d.parent_code]))
    const underNot = c => { const seen = new Set(); for (let x = c; x && !seen.has(x); x = parentOf.get(x)) { if (notCodes.has(x)) return true; seen.add(x) } return false }
    for (const t of tokens) {
      if ((t.type !== 'homecity' && t.type !== 'center') || !t.value.codes) continue
      const codes = t.value.codes.filter(c => !underNot(c))
      if (codes.length !== t.value.codes.length) Object.assign(t, { value: { ...t.value, codes }, patch: BROWSE_POLICY[t.type]({ ...t.value, codes }) })
    }
  }
  const dropToken = i => { const t = tokens[i]; dropped.push({ start: t.start, end: t.end, sig: t.sig }); tokens.splice(i, 1) }
  const isInclude = t => t.type === 'district' && !t.value.negated
  if (tokens.some(t => isInclude(t) && /^(MUN_|TWN_)/.test(t.value.code))) {
    // v20 extends this to the home-city token: "ბათუმი თბილისი" is contradictory, and
    // injecting 63 Tbilisi districts into a Batumi search is exactly what this rule guards.
    for (let i = tokens.length - 1; i >= 0; i--) if (tokens[i].type === 'center' || tokens[i].type === 'homecity') dropToken(i)
    // v31: the same contradiction one level down — "ბათუმში ზღვასთან" (by the sea, in Batumi) read ზღვასთან as the
    // Tbilisi Sea area and searched Batumi AND a Tbilisi suburb. A Tbilisi-side place beside a named far city is a
    // misreading of an ordinary word: the token goes, and so does its span, so the word shows as not understood.
    for (let i = tokens.length - 1; i >= 0; i--) {
      const t = tokens[i]
      if (t.type !== 'district' || /^(MUN_|TWN_)/.test(t.value.code)) continue
      if (![t.value.code, ...(t.value.codes || [])].every(c => TBILISI_CODE_PREFIXES.some(p => String(c).startsWith(p)))) continue
      tokens.splice(i, 1)
      const at = consumed.findIndex(c => c.start === t.start && c.end === t.end)
      if (at >= 0) consumed.splice(at, 1)
    }
  }
  // v28: "vake tbilisi" — a Tbilisi district already says the city, so the city word is understood (its span stays
  // consumed) and adds nothing; applied, it would widen the Desk's district union to every Tbilisi district.
  // (2026-10-22: only an INCLUDED district says the city — "არა ვაკე თბილისი" is Tbilisi without Vake)
  if (tokens.some(t => isInclude(t) && [t.value.code, ...(t.value.codes || [])].some(c => TBILISI_CODE_PREFIXES.some(p => String(c).startsWith(p))))) {
    for (let i = tokens.length - 1; i >= 0; i--) if (tokens[i].type === 'homecity') dropToken(i)
  }

  // v30: a district token with a numbered type next to it is that sub-area ("გლდანის მე-3 მ/რ" → Gldani 3rd Microdistrict)
  if (tokens.some(t => isInclude(t) && !t.value.codes)) {
    const kids = numberedChildren(districts)
    for (const t of tokens) {
      if (!isInclude(t) || t.value.codes) continue
      let span = null, pairs = null
      const a = AREA_AFTER.exec(lower.slice(t.end))
      if (a) { pairs = areaPairs(a[0]); span = [t.start, t.end + a[0].length] }
      else {
        const b = AREA_BEFORE.exec(lower.slice(0, t.start))
        if (b) { pairs = areaPairs(b[1]); span = [t.start - b[1].length, t.end] }
      }
      if (!pairs?.length) continue
      const kid = kids.get(t.value.code + '|' + pairs.join(','))
      if (!kid) continue
      // the pair's words are this token's now: nothing else keeps them (a floor or price reading of "3")
      for (let i = tokens.length - 1; i >= 0; i--) if (tokens[i] !== t && tokens[i].start >= span[0] && tokens[i].end <= span[1]) tokens.splice(i, 1)
      Object.assign(t, { sig: `district:${kid.code}`, label: `📍 ${kid.display}`, value: { code: kid.code, display: kid.display },
        patch: BROWSE_POLICY.district({ code: kid.code, display: kid.display }), start: span[0], end: span[1], matched: raw.slice(span[0], span[1]) })
      consumed.push({ start: span[0], end: span[1] })
    }
  }

  // residual = original text minus consumed spans (kept in original casing) —
  // it stays a plain text search AND it's the log's "what we didn't understand".
  // `failures` keeps the unclaimed CHUNKS as structured records with an honest
  // mechanical tag: 'unclaimed_number' (a bare number no unit word explained) vs
  // 'unclaimed_text'. Deliberately NOT guessing semantic reasons (unknown_alias /
  // unsupported_concept) — that's what the human review of the log determines;
  // auto-assigning it would put fake precision into the training data.
  let residual = ''
  const failures = []
  let pos = 0
  const chunks = []
  for (const c of [...consumed].sort((a, b) => a.start - b.start)) {
    chunks.push(raw.slice(pos, c.start))
    pos = Math.max(pos, c.end)
  }
  chunks.push(raw.slice(pos))
  // v32: "500$-ში ბინა" left "-ში" — a hyphen + case ending glued to the text before it belongs to that text
  for (let i = 1; i < chunks.length; i++) chunks[i] = chunks[i].replace(/^-(ში|ზე|ად|ით|დან|მდე|ამდე|თვის|იანი)(?=\s|$)/u, '')
  // v38: the same for a Turkish case ending after an apostrophe ("Gori'de arsa" left "'de")
  for (let i = 1; i < chunks.length; i++) chunks[i] = chunks[i].replace(/^['’](de|da|te|ta|deki|daki|teki|taki|den|dan|ten|tan|e|a|ye|ya)(?=\s|$)/iu, '')
  // Engine v14: stopwords (რაიონში, ან, და…) are dropped from the residual — it feeds an
  // AND-per-token text search, where one glue word zeroes the whole query — but still
  // logged in failures, so the query log keeps the full picture of what was typed.
  const stopSet = new Set(SEARCH_STOPWORDS)
  for (const chunk of chunks) {
    const frag = chunk.replace(/\s+/g, ' ').trim()
    if (!frag) continue
    const kept = frag.split(' ').filter(w => {
      // v26: a fragment of pure punctuation ("2bed,600$,saburtalo" left ", ,") is not a search word
      if (!/[\p{L}\p{N}]/u.test(w)) { failures.push({ fragment: w, reason: 'punctuation' }); return false }
      if (!stopSet.has(w.toLowerCase())) return true
      failures.push({ fragment: w, reason: 'stopword' })
      return false
    }).join(' ')
    if (!kept) continue
    residual += (residual ? ' ' : '') + kept
    failures.push({ fragment: kept, reason: /^\d[\d.,\s]*$/.test(kept) ? 'unclaimed_number' : 'unclaimed_text' })
  }

  // v39 (owner 2026-10-31, filters audit): on the Desk, "2 room vake under 200k" with no deal word searched rentals too (a
  // 1,200 USD/month flat is under 200,000). A price no rent reaches is a sale price: its chip also sets the deal (one chip,
  // so removing it removes both). A deal word in the text always wins. ⚠️ No number here (owner: "we dont hardcode any
  // numbers — it must be changed by data"): the caller's isSalePrice(amount, currency|null) decides from the market's own
  // rent prices; without it nothing is inferred. Public mode has its own rule (v26/v27 bare-number bands).
  if (!publicMode && isSalePrice && !tokens.some(t => t.type === 'deal')) {
    for (const t of tokens) {
      if (t.type !== 'price' || !isSalePrice(Number(t.value.amount), t.value.currencySpoken ? t.value.currency : null)) continue
      Object.assign(t, { label: `${t.label} · sale`, value: { ...t.value, deal: 'sale' }, patch: { ...t.patch, intents: ['sale'] } })
    }
  }

  // ── Stage 4 (SEARCH_GRAMMAR §1): the compilation IS a sentence, not a bag of patches. ──
  // The merged token patches form a flat filters-shaped object; toSentence() projects it
  // into grammar slots. Array patches UNION (two districts = one location slot), scalars
  // last-write-win exactly as the advFilters diff effect applies them — the sentence must
  // describe what the patches will actually DO, not a private reading of them. The residual
  // rides as `text`: unclaimed words are part of what the agent asked.
  const mergedPatch = {}
  for (const tk of tokens) {
    for (const [k, v] of Object.entries(tk.patch)) {
      mergedPatch[k] = Array.isArray(v) && Array.isArray(mergedPatch[k])
        ? [...new Set([...mergedPatch[k], ...v])] : v
    }
  }
  const sentence = toSentence({ ...mergedPatch, q: residual })

  return {
    tokens, residual, failures, sentence,
    // v36: understood spans with no token of their own — only those still consumed (a span a rule gave back is residual)
    dropped: dropped.filter(d => consumed.some(c => c.start <= d.start && d.end <= c.end) && !tokens.some(t => t.start <= d.start && d.end <= t.end)),
    vocabVersion: VOCAB_VERSION, vocabHash: VOCAB_HASH, engineVersion: ENGINE_VERSION,
    ms: Math.round(((performance.now?.() ?? Date.now()) - t0) * 10) / 10,
  }
}
